import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { resolveCountryFlag } from '../../core/store-config-country';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { StoreService } from 'src/app/store.service';
import { forkJoin } from 'rxjs';
import { ModalDismissReasons, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { StoreInfoService } from 'src/app/store-info.service';

const DEFAULT_CUSTOM_HEADER_HTML = `<div style="background:linear-gradient(120deg,#0f172a,#3730a3);border-radius:14px;padding:22px;color:#fff;">
  <div style="font-size:12px;letter-spacing:0.14em;opacity:0.7;text-transform:uppercase;">Limited Offer</div>
  <div style="font-size:22px;font-weight:800;margin-top:6px;">Summer Bundle Sale</div>
  <div style="font-size:13px;opacity:0.85;margin-top:6px;">Free shipping on all orders today</div>
</div>`;

// Custom Footer sits at the bottom of the checkout page. The sample is a
// high-converting trust & social proof footer — trust badges, customer ratings,
// verified testimonials, and direct support contact to boost checkout conversions.
const DEFAULT_CUSTOM_FOOTER_HTML = `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;max-width:900px;margin:16px auto 0;color:#1e293b;line-height:1.5;">

  <!-- Trust Badges & Guarantees -->
  <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:16px 12px;margin-bottom:12px;display:flex;flex-wrap:wrap;gap:12px;justify-content:space-around;text-align:center;">
    <div style="flex:1 1 95px;min-width:90px;display:flex;flex-direction:column;align-items:center;gap:3px;">
      <span style="font-size:20px;">🔒</span>
      <div style="font-size:12px;font-weight:700;color:#0f172a;">Secure Payment</div>
      <div style="font-size:11px;color:#64748b;">256-Bit SSL Encrypted</div>
    </div>
    <div style="flex:1 1 95px;min-width:90px;display:flex;flex-direction:column;align-items:center;gap:3px;">
      <span style="font-size:20px;">🛡️</span>
      <div style="font-size:12px;font-weight:700;color:#0f172a;">100% Authentic</div>
      <div style="font-size:11px;color:#64748b;">Genuine Guaranteed</div>
    </div>
    <div style="flex:1 1 95px;min-width:90px;display:flex;flex-direction:column;align-items:center;gap:3px;">
      <span style="font-size:20px;">🚚</span>
      <div style="font-size:12px;font-weight:700;color:#0f172a;">Fast Shipping</div>
      <div style="font-size:11px;color:#64748b;">Tracked Delivery</div>
    </div>
  </div>

  <!-- Social Proof & Customer Reviews -->
  <div style="background:#ffffff;border:1px solid #e2e8f0;border-radius:12px;padding:16px;margin-bottom:12px;box-shadow:0 1px 3px rgba(0,0,0,0.03);">
    <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;margin-bottom:12px;padding-bottom:10px;border-bottom:1px solid #f1f5f9;">
      <div>
        <div style="font-size:13px;font-weight:700;color:#0f172a;">Rated 4.9/5 by 2,400+ Customers</div>
        <div style="color:#f59e0b;font-size:12px;margin-top:2px;letter-spacing:1px;">★★★★★ <span style="color:#475569;font-size:11px;font-weight:600;letter-spacing:normal;margin-left:4px;">Verified Feedback</span></div>
      </div>
      <span style="background:#ecfdf5;color:#059669;font-size:11px;font-weight:600;padding:3px 9px;border-radius:20px;border:1px solid #a7f3d0;">
        ✓ Verified Reviews
      </span>
    </div>

    <!-- Review Cards -->
    <div style="display:flex;flex-direction:column;gap:10px;">
      <div style="background:#f8fafc;border-radius:10px;padding:12px;border:1px solid #f1f5f9;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;">
          <span style="font-size:12px;font-weight:700;color:#0f172a;">Sarah L.</span>
          <span style="font-size:10.5px;color:#10b981;font-weight:600;">✓ Verified Buyer</span>
        </div>
        <div style="color:#f59e0b;font-size:11px;margin-bottom:5px;">★★★★★</div>
        <p style="font-size:11.5px;color:#475569;margin:0;line-height:1.45;">"Super fast delivery! Received my order in 2 days in perfect condition. Authentic product and hassle-free checkout."</p>
      </div>

      <div style="background:#f8fafc;border-radius:10px;padding:12px;border:1px solid #f1f5f9;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;">
          <span style="font-size:12px;font-weight:700;color:#0f172a;">Michael T.</span>
          <span style="font-size:10.5px;color:#10b981;font-weight:600;">✓ Verified Buyer</span>
        </div>
        <div style="color:#f59e0b;font-size:11px;margin-bottom:5px;">★★★★★</div>
        <p style="font-size:11.5px;color:#475569;margin:0;line-height:1.45;">"Smooth order process and great customer support when I asked about shipping times. Will definitely purchase again!"</p>
      </div>
    </div>
  </div>

  <!-- Help & Support Line -->
  <div style="background:#f1f5f9;border-radius:10px;padding:12px 14px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;font-size:11.5px;color:#475569;">
    <div style="display:flex;align-items:center;gap:6px;">
      <span style="font-size:15px;">💬</span>
      <span><strong>Questions about your order?</strong> We're here to help.</span>
    </div>
    <span style="color:#2563eb;font-weight:600;">support@yourstore.com</span>
  </div>

  <!-- Security Guarantee Subtext -->
  <div style="text-align:center;font-size:11px;color:#94a3b8;margin-top:10px;">
    🔒 100% Secure Checkout • All Data Encrypted & Protected
  </div>

</div>`;


// Custom Highlight sits above the voucher field on the checkout page, so the
// sample is a programme breakdown — the thing dealers hand-build there most
// often. Shipped as the starting value (and as "Reset to sample") so the
// dealer edits a working layout instead of an empty box.
const DEFAULT_CUSTOM_HIGHLIGHT_HTML = `<div style="font-family:Arial,Helvetica,sans-serif;max-width:900px;margin:0 auto;color:#222;">

  <div style="font-size:16px;font-weight:700;margin-bottom:6px;margin-top:10px">
    Included in your programme
  </div>

  <div style="display:flex;align-items:center;justify-content:space-between;padding:14px 0;border-bottom:1px solid #eee;">
    <div style="display:flex;align-items:center;font-size:12px;">
      <span style="font-size:16px;margin-right:12px;">✓</span>
      <span>Kure Oiru 60s</span>
    </div>
    <div style="font-size:14px;font-weight:600;">$350</div>
  </div>

  <div style="display:flex;align-items:center;justify-content:space-between;padding:14px 0;border-bottom:1px solid #eee;">
    <div style="display:flex;align-items:center;font-size:12px;">
      <span style="font-size:16px;margin-right:12px;">✓</span>
      <span>Kuro Mizu 30s</span>
    </div>
    <div style="font-size:14px;font-weight:600;">$380</div>
  </div>

  <div style="display:flex;align-items:center;justify-content:space-between;padding:14px 0;border-bottom:1px solid #eee;">
    <div style="display:flex;align-items:center;font-size:12px;">
      <span style="font-size:16px;margin-right:12px;">✓</span>
      <span>Kuro Shuga 60s</span>
    </div>
    <div style="font-size:14px;font-weight:600;">$350</div>
  </div>

  <div style="display:flex;align-items:center;justify-content:space-between;padding:14px 0;border-bottom:1px solid #eee;">
    <div style="display:flex;align-items:center;font-size:12px;">
      <span style="font-size:16px;margin-right:12px;">✓</span>
      <span>Imuglo</span>
    </div>
    <div style="font-size:14px;font-weight:600;">$250</div>
  </div>

  <div style="display:flex;align-items:center;justify-content:space-between;padding:14px 0;border-bottom:1px solid #eee;">
    <div style="display:flex;align-items:center;font-size:12px;">
      <span style="font-size:16px;margin-right:12px;">✓</span>
      <span>Loquat</span>
    </div>
    <div style="font-size:14px;font-weight:600;">$250</div>
  </div>

  <div style="display:flex;align-items:center;justify-content:space-between;padding:14px 0;">
    <div style="display:flex;align-items:center;font-size:12px;">
      <span style="font-size:16px;margin-right:12px;">✓</span>
      <span>Livetal</span>
    </div>
    <div style="font-size:14px;font-weight:600;">$250</div>
  </div>

</div>`;

// Custom Note sits under the order total, so the sample is a post-purchase
// walkthrough — what happens next, which is what dealers put there. Shipped as
// the starting value (and as "Reset to sample") like the Custom Highlight one.
const DEFAULT_CUSTOM_NOTE_HTML = `<div style="font-family:Arial,Helvetica,sans-serif;max-width:900px;margin:0 auto;color:#222;">

  <div style="font-size:16px;font-weight:700;margin-bottom:10px; margin-top:10px">
    What to do after your purchase
  </div>

  <div style="display:flex;align-items:flex-start;padding:14px 0;border-bottom:1px solid #eee;">
    <div style="min-width:30px;height:30px;border-radius:50%;background:#222;color:#fff;font-size:13px;font-weight:700;text-align:center;line-height:30px;margin-right:12px;">
      1
    </div>
    <div>
      <div style="font-size:13px;font-weight:700;margin-bottom:5px;">
        Check your order confirmation
      </div>
      <div style="font-size:12px;color:#666;line-height:1.5;">
        After completing your purchase, check your email or order page for your order details and confirmation.
      </div>
    </div>
  </div>

  <div style="display:flex;align-items:flex-start;padding:14px 0;border-bottom:1px solid #eee;">
    <div style="min-width:30px;height:30px;border-radius:50%;background:#222;color:#fff;font-size:13px;font-weight:700;text-align:center;line-height:30px;margin-right:12px;">
      2
    </div>
    <div>
      <div style="font-size:13px;font-weight:700;margin-bottom:5px;">
        Wait for your delivery
      </div>
      <div style="font-size:12px;color:#666;line-height:1.5;">
        Your order will be prepared and shipped to the delivery address provided during checkout.
      </div>
    </div>
  </div>

  <div style="display:flex;align-items:flex-start;padding:14px 0;border-bottom:1px solid #eee;">
    <div style="min-width:30px;height:30px;border-radius:50%;background:#222;color:#fff;font-size:13px;font-weight:700;text-align:center;line-height:30px;margin-right:12px;">
      3
    </div>
    <div>
      <div style="font-size:13px;font-weight:700;margin-bottom:5px;">
        Receive and check your products
      </div>
      <div style="font-size:12px;color:#666;line-height:1.5;">
        Once your order arrives, check that all products are included and in good condition.
      </div>
    </div>
  </div>

  <div style="display:flex;align-items:flex-start;padding:14px 0;">
    <div style="min-width:30px;height:30px;border-radius:50%;background:#222;color:#fff;font-size:13px;font-weight:700;text-align:center;line-height:30px;margin-right:12px;">
      4
    </div>
    <div>
      <div style="font-size:13px;font-weight:700;margin-bottom:5px;">
        Start using your products
      </div>
      <div style="font-size:12px;color:#666;line-height:1.5;">
        Follow the recommended usage instructions and enjoy your products. Contact our support team if you need any help.
      </div>
    </div>
  </div>

</div>`;

// How the checkout page picks its Recommended Add-ons. 'auto' lets the
// storefront suggest products from the same series as the cart items; 'manual'
// uses the dealer's hand-picked list.
const RECOMMENDATION_MODE_AUTO = 'auto';
const RECOMMENDATION_MODE_MANUAL = 'manual';

type ListingViewMode = 'tile' | 'list';

// PWP (Purchase With Purchase) — what the link saves per promotion product in
// `option_json.pwp_products`: how many the checkout page pre-selects and
// whether the row is shown there. Keyed by promotion + product because one
// product can be the action of two promotions.
interface PwpProductSetting {
  promotion_id: number;
  product_id: number;
  quantity: number;
  visible: boolean;
  // Quantity Lock for the PWP row: the checkout page shows the pre-selected
  // quantity without − / +. Only takes effect with a quantity above 0.
  locked: boolean;
  // The bundle's pick products, where the PWP product is a mixture the
  // customer chooses from: how many of each are picked up front and whether
  // the option is offered at all.
  picks: PwpPickSetting[];
}

interface PwpPickSetting {
  product_id: number;
  quantity: number;
  visible: boolean;
  // Quantity Lock for the option: the picker shows its quantity without
  // − / +. Only takes effect with a quantity above 0.
  locked: boolean;
}

// A selectable group of a PWP bundle: the customer picks `per_unit` × the PWP
// quantity from `products`. Groups with a single product are fixed and need
// no setting.
interface PwpPickGroup {
  group: string;
  per_unit: number;
  products: { product_id: number; product_name: string; image: string }[];
}

// One row of the PWP card: a promotion product the selected products unlock,
// resolved against the store catalogue, plus the products that unlock it.
interface PwpEntry {
  promotion_id: number;
  promotion_name: string;
  product_id: number;
  product_name: string;
  image: string;
  price: number;
  currency: string;
  currency_symbol: string;
  max_qty: number;
  trigger_names: string[];
  // The promotion's own condition: the checkout page offers this PWP only
  // once the customer has `min_qty` of the trigger products; `trigger_qty`
  // is how many this link starts them with.
  min_qty: number;
  trigger_qty: number;
  pick_groups: PwpPickGroup[];
}

type CustomHtmlBlock = 'header' | 'footer' | 'highlight' | 'note';

// What the HTML editor popup needs to know about one custom HTML block.
// `title` and `resetLabel` are translation keys, the same ones its card uses.
interface CustomHtmlEditor {
  controlName: string;
  title: string;
  resetLabel: string;
  reset: () => void;
  preview: () => SafeHtml;
}

@Component({
  selector: 'app-list-stores',
  templateUrl: './express-checkout-create.component.html',
  styleUrls: ['./express-checkout-create.component.scss'],
})
export class ExpressCheckoutCreateComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('pageHeader') pageHeader: ElementRef<HTMLElement>;
  showFloatingCta = false;
  private headerObserver?: IntersectionObserver;

  bearer;
  store_id;
  checkout_list = [];
  country;
  selectedCountry;
  language_name;
  isChecks = false;
  selectedProducts: { product_id: number; quantity: number }[] = [];
  // Quantity Lock — the selected products whose quantity the customer may not
  // change. Held as a plain id list rather than a flag on `selectedProducts`
  // because the two are restored in opposite order on the edit boot:
  // option_json (this list) lands before the products themselves do.
  lockedQuantityProductIds: number[] = [];
  // PWP — the store's active promotions, fetched once and matched against
  // `selectedProducts` on the fly so the card appears the moment a product
  // that unlocks one is picked.
  storePromotions: any[] = [];
  // Per PWP product: pre-selected quantity + shown-or-hidden. Held apart from
  // the promotions so a saved option_json, which lands before the promotions
  // load, can be restored first.
  pwpProductSettings: PwpProductSetting[] = [];
  // Which tab the Product Quantity Settings card shows. The PWP tab only
  // exists while a selected product unlocks a promotion; the template falls
  // back to the product tab on its own when that stops being true.
  quantityTab: 'products' | 'pwp' = 'products';
  // Product Recommendations — add-on products offered on the checkout page.
  // Deliberately disjoint from `selectedProducts`: a recommendation is a
  // product the link did NOT put in the cart, so the two lists never overlap.
  productRecommendations = [];
  productRecommendationFilter: string = '';
  readonly MAX_PRODUCT_RECOMMENDATIONS = 5;
  unavailableProducts = [];
  mixture_data = [];
  body = {};
  urlData;
  expressUrl;
  checkoutSuccess = false;
  buttonFreeze = false;
  checkout_link_id;
  nexusSsoLink: string = null;
  isSvoStore = false;
  isStoreInfoLoading = true;



  filteredString: string = '';
  filteredCountry: string = '';
  activatedCountry: any[] = [];

  currentLanguage: any = [];
  languageList: any[] = [];
  filteredStatus: string = '';
  filteredStatusDisplay: string = '';
  displayedProducts: any[] = [];
  products: any[] = [];
  language_code;
  updateLink = false;
  // Country + language are locked while editing an existing checkout.
  isEditMode = false;
  // True while an existing checkout's data is still loading (edit mode only).
  isEditLoading = false;
  countryFirstInit: boolean = false;
  countryLoaded: boolean = false;

  expressCheckoutData: any = [];
  expressCheckoutDataByLanguage = {};

  page_title;
  url;
  description;

  is_subscription = 0;
  subscription = 'Products';
  subscription_status = ['Products', 'Subscriptions'];
  showSubscriptionTab = true;
  subscription_view = true;
  subscriptionApiProducts: any[] = [];
  subscriptionApiLoading = false;
  subscriptionApiLoaded = false;
  subscriptionApiLoadPromise: Promise<void> | null = null;

  updatingDisplayQueue = [];
  listingView: ListingViewMode = 'list';

  public initialArrangement = []; // Initially loaded or unchanged arrangement
  public productArrangement = []; // Arrangement state for current listing
  public customArrangement = {}; // Arrangement from backend, multi country
  customArrangementReady = false;
  isSavingArrangement = false;

  loading = true;
  closeResult = '';
  success = 'success';
  generateForm: FormGroup;

  productFilter: string = '';
  filteredSubscription: string = '';
  toggleState: boolean = false;
  subscriptionProduct = false;
  selectedSubscriptionTab = false;

  // Product Link — domains are loaded from the API when the toggle is enabled.
  showProductLink = false;
  selectedProductLinkDomain = '';
  productLinkDomains: string[] = [];
  productLinkLoading = false;

  // Product Link result shown on the success page after a link is created.
  showProductLinkResult = false;
  productLinkResultUrl = '';     // full product URL (domain_product_url) — used for the link/copy
  productLinkResultDisplay = ''; // display text (domain_url)

  toggleProductLink() {
    this.showProductLink = !this.showProductLink;
    if (!this.showProductLink) {
      this.selectedProductLinkDomain = '';
      return;
    }
    this.fetchProductLinkDomains();
  }

  selectProductLinkDomain(domain: string) {
    this.selectedProductLinkDomain = domain;
  }

  fetchProductLinkDomains() {
    const body = {
      store_id: +this.store_id,
      language_code: this.language_code,
      country_code: this.getSelectedCountryCode,
    };

    this.productLinkLoading = true;
    this.svc.listCompanyDomain(body, this.bearer).subscribe(
      (res) => {
        // Response shape: { data: { urls: ["https://...", ...] } }
        const urls = res?.['data']?.['urls'] ?? [];
        this.productLinkDomains = (Array.isArray(urls) ? urls : [])
          .map((item) =>
            typeof item === 'string' ? item : item?.url ?? item?.domain ?? ''
          )
          .filter(Boolean);
        this.productLinkLoading = false;
      },
      (error) => {
        console.log('Product link domain error:', error);
        this.productLinkDomains = [];
        this.productLinkLoading = false;
      }
    );
  }

  // Restore the Product Link toggle + selected domain when editing an existing
  // checkout: product_link_status === 1, domain from product_link_domain_info.
  restoreProductLink(checkoutData) {
    if (+checkoutData?.product_link_status !== 1) return;
    // Current API returns the selected domain directly on the record as
    // product_domain_url (base domain that matches a dropdown option).
    let domainUrl = checkoutData?.product_domain_url;
    // Legacy fallback: product_link_domain_info as an object or JSON string,
    // e.g. '{"unique_id":"cT3Zuogq","domain_url":"https://esvio-store.fiv5s.dev"}'
    if (!domainUrl) {
      let info = checkoutData?.product_link_domain_info;
      if (typeof info === 'string') {
        try { info = JSON.parse(info); } catch (e) { info = null; }
      }
      domainUrl = Array.isArray(info) ? info?.[0]?.domain_url : info?.domain_url;
    }
    this.showProductLink = true;
    this.selectedProductLinkDomain = domainUrl ?? '';
    this.fetchProductLinkDomains();
  }

  // Adds domain_url / domain_status to the create/update payload.
  // domain_status = 1 only when the toggle is on, a domain is selected, and the
  // feature applies to this country (Product Link is hidden for the TWTW store).
  private applyProductLinkToBody() {
    const hasDomain =
      !this.isTaiwanLocal &&
      this.showProductLink &&
      !!this.selectedProductLinkDomain;
    this.body['domain_status'] = hasDomain ? 1 : 0;
    this.body['domain_url'] = hasDomain ? this.selectedProductLinkDomain : '';
  }

  // After a successful create, capture the product link URL so the success page
  // can show it. Only when the user enabled Product Link + picked a domain.
  private captureProductLinkResult(data: any) {
    const chosen =
      !this.isTaiwanLocal &&
      this.showProductLink &&
      !!this.selectedProductLinkDomain;
    this.showProductLinkResult = chosen;
    this.productLinkResultUrl = '';
    this.productLinkResultDisplay = '';
    if (!chosen) return;

    // Prefer the values returned by the create response (siblings of express_url).
    // domain_url is "domain/slug"; show/open/copy it as "domain/go/slug".
    const domainUrl = data?.['domain_url'] || data?.['domain_product_url'] || '';
    if (domainUrl) {
      const goUrl = this.productGoUrl(domainUrl);
      this.productLinkResultUrl = goUrl;
      this.productLinkResultDisplay = goUrl;
      return;
    }
    // Fallback: the create response didn't include the domain fields — fetch the
    // freshly created link from the list endpoint, which carries both.
    this.resolveProductLinkResultFromList();
  }

  // The product link URL shown / copied / opened on the success page.
  // domain_url is "domain/slug"; insert a /go/ redirect segment -> "domain/go/slug".
  private productGoUrl(domainUrl: string): string {
    if (!domainUrl) return '';
    try {
      const url = new URL(domainUrl);
      const slug = url.pathname.replace(/^\/+/, '');
      return `${url.origin}/go/${slug}`;
    } catch (e) {
      return domainUrl;
    }
  }

  private resolveProductLinkResultFromList() {
    if (!this.checkout_link_id) {
      this.productLinkResultDisplay = this.selectedProductLinkDomain;
      this.productLinkResultUrl = this.selectedProductLinkDomain;
      return;
    }
    this.svc.getCheckoutLinkLists(this.bearer, this.store_id).subscribe(
      (res) => {
        const list = Array.isArray(res?.['data']) ? res['data'] : [];
        let match = null;
        for (const item of list) {
          for (const link of item?.link_list || []) {
            if (
              +link?.checkout_link_id === +this.checkout_link_id &&
              link?.domain_url
            ) {
              match = link;
              // Prefer the variant matching the created language.
              if (link?.language_code === this.language_code) break;
            }
          }
          if (match?.language_code === this.language_code) break;
        }
        if (match) {
          const goUrl = this.productGoUrl(match['domain_url']);
          this.productLinkResultUrl = goUrl;
          this.productLinkResultDisplay = goUrl;
        } else {
          this.productLinkResultDisplay = this.selectedProductLinkDomain;
          this.productLinkResultUrl = this.selectedProductLinkDomain;
        }
      },
      (error) => {
        console.log('Resolve product link url error:', error);
        this.productLinkResultDisplay = this.selectedProductLinkDomain;
        this.productLinkResultUrl = this.selectedProductLinkDomain;
      }
    );
  }

  buildForm() {
    const formSchema = {
      page_title: [''],
      url: [''],
      description: [''],
      voucher_status: [true],
      custom_header_enabled: [false],
      custom_header_html: [DEFAULT_CUSTOM_HEADER_HTML],
      custom_footer_enabled: [false],
      custom_footer_html: [DEFAULT_CUSTOM_FOOTER_HTML],
      custom_highlight_enabled: [false],
      custom_highlight_html: [DEFAULT_CUSTOM_HIGHLIGHT_HTML],
      custom_note_enabled: [false],
      custom_note_html: [DEFAULT_CUSTOM_NOTE_HTML],
      product_recommendations: [true],
      product_recommendations_mode: [RECOMMENDATION_MODE_AUTO],
    };

    this.appearanceOptionSchema.forEach((schema) => {
      formSchema[schema.formName] = [''];
    });

    this.generateForm = this.formBuilder.group(formSchema);
  }

  resetCustomHeaderHtml() {
    this.generateForm.get('custom_header_html').setValue(DEFAULT_CUSTOM_HEADER_HTML);
  }

  resetCustomFooterHtml() {
    this.generateForm.get('custom_footer_html').setValue(DEFAULT_CUSTOM_FOOTER_HTML);
  }

  clearCustomFooterHtml() {
    this.generateForm.get('custom_footer_html').setValue('');
  }

  resetCustomHighlightHtml() {
    this.generateForm
      .get('custom_highlight_html')
      .setValue(DEFAULT_CUSTOM_HIGHLIGHT_HTML);
  }

  resetCustomNoteHtml() {
    this.generateForm.get('custom_note_html').setValue(DEFAULT_CUSTOM_NOTE_HTML);
  }

  // Angular's default [innerHTML] sanitizer strips the style attribute entirely,
  // which breaks this feature's "inline styles are supported" requirement. This
  // HTML is only ever authored by the store's own authenticated dealer/admin via
  // this form, so it's trusted the same way Appearance Settings colors already are.
  //
  // Memoized: bypassSecurityTrustHtml() must not create a new SafeHtml instance on
  // every change-detection run, or the [innerHTML] binding sees a "changed" value
  // each cycle and keeps reassigning innerHTML, causing constant re-render/flicker.
  private _lastCustomHeaderHtml: string;
  private _lastSafeCustomHeaderHtml: SafeHtml;
  getSafeCustomHeaderHtml(): SafeHtml {
    const html = this.generateForm.get('custom_header_html')?.value ?? '';
    if (html !== this._lastCustomHeaderHtml) {
      this._lastCustomHeaderHtml = html;
      this._lastSafeCustomHeaderHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    }
    return this._lastSafeCustomHeaderHtml;
  }

  private _lastCustomFooterHtml: string;
  private _lastSafeCustomFooterHtml: SafeHtml;
  getSafeCustomFooterHtml(): SafeHtml {
    const html = this.generateForm.get('custom_footer_html')?.value ?? '';
    if (html !== this._lastCustomFooterHtml) {
      this._lastCustomFooterHtml = html;
      this._lastSafeCustomFooterHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    }
    return this._lastSafeCustomFooterHtml;
  }

  get hasCustomFooterContent(): boolean {
    return !!this.generateForm.get('custom_footer_html')?.value?.trim();
  }

  get showCustomFooterPreview(): boolean {
    return (
      !!this.generateForm.get('custom_footer_enabled')?.value &&
      !!this.generateForm.get('custom_footer_html')?.value
    );
  }

  private _lastCustomHighlightHtml: string;
  private _lastSafeCustomHighlightHtml: SafeHtml;
  getSafeCustomHighlightHtml(): SafeHtml {
    const html = this.generateForm.get('custom_highlight_html')?.value ?? '';
    if (html !== this._lastCustomHighlightHtml) {
      this._lastCustomHighlightHtml = html;
      this._lastSafeCustomHighlightHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    }
    return this._lastSafeCustomHighlightHtml;
  }

  // The preview only draws the block when the toggle is on AND there is markup
  // to draw, mirroring how the storefront gates it. Off for both Taiwan stores
  // (TWTW and MYTW), where the feature isn't offered and the card is hidden.
  get showCustomHighlightPreview(): boolean {
    return (
      !this.isTaiwan &&
      !!this.generateForm.get('custom_highlight_enabled')?.value &&
      !!this.generateForm.get('custom_highlight_html')?.value
    );
  }

  private _lastCustomNoteHtml: string;
  private _lastSafeCustomNoteHtml: SafeHtml;
  getSafeCustomNoteHtml(): SafeHtml {
    const html = this.generateForm.get('custom_note_html')?.value ?? '';
    if (html !== this._lastCustomNoteHtml) {
      this._lastCustomNoteHtml = html;
      this._lastSafeCustomNoteHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    }
    return this._lastSafeCustomNoteHtml;
  }

  // Gated exactly like showCustomHighlightPreview — toggle on, markup present,
  // and not a Taiwan store.
  get showCustomNotePreview(): boolean {
    return (
      !this.isTaiwan &&
      !!this.generateForm.get('custom_note_enabled')?.value &&
      !!this.generateForm.get('custom_note_html')?.value
    );
  }

  // Voucher input visibility in live preview and checkout.
  get showVoucherCode(): boolean {
    return this.generateForm.get('voucher_status')?.value ?? true;
  }

  // Active tab within the Custom HTML Content card.
  activeCustomTab: CustomHtmlBlock = 'header';

  selectCustomTab(tab: CustomHtmlBlock) {
    this.activeCustomTab = tab;
  }

  get currentCustomTab(): CustomHtmlBlock {
    if (this.isTaiwan && (this.activeCustomTab === 'highlight' || this.activeCustomTab === 'note')) {
      return 'header';
    }
    return this.activeCustomTab;
  }

  isCustomBlockActive(block: CustomHtmlBlock): boolean {
    if (!this.generateForm) return false;
    switch (block) {
      case 'header':
        return !!this.generateForm.get('custom_header_enabled')?.value;
      case 'highlight':
        return !this.isTaiwan && !!this.generateForm.get('custom_highlight_enabled')?.value;
      case 'note':
        return !this.isTaiwan && !!this.generateForm.get('custom_note_enabled')?.value;
      case 'footer':
        return !!this.generateForm.get('custom_footer_enabled')?.value;
      default:
        return false;
    }
  }

  // Long markup is hard to work on in a card's 180px textarea, so each custom
  // HTML block can also be edited in a full-height popup, next to its own
  // preview (the phone preview is covered while it's open). It binds the same
  // form control as the card's textarea, so edits land as you type and closing
  // it just returns to the page — there is nothing to apply or discard.
  private readonly customHtmlEditors: Record<CustomHtmlBlock, CustomHtmlEditor> = {
    header: {
      controlName: 'custom_header_html',
      title: 'Custom header HTML',
      resetLabel: 'Reset to sample',
      reset: () => this.resetCustomHeaderHtml(),
      preview: () => this.getSafeCustomHeaderHtml(),
    },
    footer: {
      controlName: 'custom_footer_html',
      title: 'Custom footer HTML',
      resetLabel: 'Reset to sample',
      reset: () => this.resetCustomFooterHtml(),
      preview: () => this.getSafeCustomFooterHtml(),
    },
    highlight: {
      controlName: 'custom_highlight_html',
      title: 'Custom highlight HTML',
      resetLabel: 'Reset to sample',
      reset: () => this.resetCustomHighlightHtml(),
      preview: () => this.getSafeCustomHighlightHtml(),
    },
    note: {
      controlName: 'custom_note_html',
      title: 'Custom note HTML',
      resetLabel: 'Reset to sample',
      reset: () => this.resetCustomNoteHtml(),
      preview: () => this.getSafeCustomNoteHtml(),
    },
  };

  @ViewChild('htmlPopupCode') htmlPopupCode: ElementRef<HTMLTextAreaElement>;
  htmlPopup: CustomHtmlEditor | null = null;
  htmlPopupControl: FormControl | null = null;
  private htmlPopupOpener: HTMLElement | null = null;
  private htmlPopupOrigOverflowY = '';
  private htmlPopupBackdropPress = false;

  // `opener` is the expand button, handed in from the click event rather than
  // read off document.activeElement: Safari doesn't focus a button on click.
  openHtmlPopup(block: CustomHtmlBlock, opener: EventTarget | null) {
    this.htmlPopup = this.customHtmlEditors[block];
    this.htmlPopupControl = this.generateForm.get(
      this.htmlPopup.controlName
    ) as FormControl;
    this.htmlPopupOpener = opener as HTMLElement;

    // Stop the page scrolling behind the popup. The page scrolls on <html>,
    // which ngOnInit sets to overflow-y: visible for the sticky preview.
    const html = this.doc.documentElement;
    this.htmlPopupOrigOverflowY = html.style.overflowY;
    html.style.overflowY = 'hidden';

    // The textarea only exists after this change detection pass renders it.
    setTimeout(() => this.htmlPopupCode?.nativeElement.focus());
  }

  closeHtmlPopup() {
    if (!this.htmlPopup) return;
    // Typing in one textarea updates the control but not the other textarea
    // bound to it (Angular skips every view on a view-to-model write), so
    // write the value back out or the card would still show the old markup.
    this.htmlPopupControl.setValue(this.htmlPopupControl.value, {
      emitEvent: false,
    });
    this.htmlPopup = null;
    this.htmlPopupControl = null;
    this.doc.documentElement.style.overflowY = this.htmlPopupOrigOverflowY;
    this.htmlPopupOpener?.focus();
    this.htmlPopupOpener = null;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.closeHtmlPopup();
  }

  // A click on the dimmed backdrop closes the popup — but only one that also
  // started there. A drag-select in the textarea that overshoots the dialog
  // lands its click on the backdrop too, and must not close it.
  onHtmlPopupBackdropMousedown(event: MouseEvent) {
    this.htmlPopupBackdropPress = event.target === event.currentTarget;
  }

  onHtmlPopupBackdropClick(event: MouseEvent) {
    if (this.htmlPopupBackdropPress && event.target === event.currentTarget) {
      this.closeHtmlPopup();
    }
    this.htmlPopupBackdropPress = false;
  }

  // Keeps Tab cycling through the popup's own controls instead of wandering
  // off to the form behind it.
  trapHtmlPopupFocus(event: KeyboardEvent, dialog: HTMLElement) {
    if (event.key !== 'Tab') return;
    const focusables = Array.from(
      dialog.querySelectorAll<HTMLElement>('.ec-html-popup__head button, .ec-html-popup__code')
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && this.doc.activeElement === first) {
      last.focus();
      event.preventDefault();
    } else if (!event.shiftKey && this.doc.activeElement === last) {
      first.focus();
      event.preventDefault();
    }
  }

  onChangeColor(event: any, type: string) {
    let selectedColor = event.target.value;
    this.appearanceOptionSchema.forEach((schema) => {
      if (schema.formName === type) {
        const ctrl = this.generateForm.get(schema.formName);
        if (ctrl) {
          ctrl.setValue(selectedColor);
          ctrl.markAsDirty();
        }
        schema.selected = selectedColor;
      }
    });
  }

  selectAppearanceColor(schema: any, color: string) {
    if (!schema?.formName) return;
    const ctrl = this.generateForm.get(schema.formName);
    if (ctrl) {
      ctrl.setValue(color);
      ctrl.markAsDirty();
    }
    schema.selected = color;
  }

  isColorInOptions(schema: any): boolean {
    const currentColor = (this.generateForm.get(schema.formName)?.value || '').toLowerCase();
    return (schema.colorOptions || []).some(
      (opt: string) => opt.toLowerCase() === currentColor
    );
  }

  // A colour is set and isn't one of the swatches. New links start with no
  // colour, which shouldn't mark the custom swatch as selected.
  isCustomColor(schema: any): boolean {
    return !!this.getCurrentColor(schema) && !this.isColorInOptions(schema);
  }

  isColorActive(schema: any, colorOption: string): boolean {
    const current = (this.generateForm.get(schema?.formName)?.value || '').toLowerCase();
    return current === (colorOption || '').toLowerCase();
  }

  getCurrentColor(schema: any): string {
    return this.generateForm.get(schema?.formName)?.value || '';
  }

  isLightColor(hex: string): boolean {
    if (!hex) return false;
    let c = hex.replace('#', '').trim();
    if (c.length === 3) {
      c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
    }
    if (c.length !== 6) return false;
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return false;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.65;
  }

  colorPresets = [
    {
      name: 'Indigo',
      preview: ['#45489D', '#45489D'],
      colors: {
        design_header_section_color: '#45489D',
        design_payment_button_color: '#45489D',
        design_main_title_color: '#ffffff',
        design_sub_text_color: '#ffffff',
      },
    },
    {
      name: 'Emerald',
      preview: ['#8DC642', '#8DC642'],
      colors: {
        design_header_section_color: '#8DC642',
        design_payment_button_color: '#8DC642',
        design_main_title_color: '#000000',
        design_sub_text_color: '#000000',
      },
    },
    {
      name: 'Sunset',
      preview: ['#FFA500', '#FFA500'],
      colors: {
        design_header_section_color: '#FFA500',
        design_payment_button_color: '#FFA500',
        design_main_title_color: '#000000',
        design_sub_text_color: '#000000',
      },
    },
    {
      name: 'Rose',
      preview: ['#DC9ED6', '#DC9ED6'],
      colors: {
        design_header_section_color: '#DC9ED6',
        design_payment_button_color: '#DC9ED6',
        design_main_title_color: '#000000',
        design_sub_text_color: '#000000',
      },
    },
    {
      name: 'Gold',
      preview: ['#E1C16E', '#E1C16E'],
      colors: {
        design_header_section_color: '#E1C16E',
        design_payment_button_color: '#E1C16E',
        design_main_title_color: '#000000',
        design_sub_text_color: '#000000',
      },
    },
    {
      name: 'Dark',
      preview: ['#000000', '#000000'],
      colors: {
        design_header_section_color: '#000000',
        design_payment_button_color: '#000000',
        design_main_title_color: '#ffffff',
        design_sub_text_color: '#ffffff',
      },
    },
  ];

  applyColorPreset(preset: any) {
    if (!preset?.colors) return;
    Object.keys(preset.colors).forEach((key) => {
      const color = preset.colors[key];
      const ctrl = this.generateForm.get(key);
      if (ctrl) {
        ctrl.setValue(color);
        ctrl.markAsDirty();
      }
      const schema = this.appearanceOptionSchema.find((s) => s.formName === key);
      if (schema) {
        schema.selected = color;
      }
    });
  }

  isColorPresetActive(preset: any): boolean {
    if (!preset?.colors) return false;
    return Object.keys(preset.colors).every((key) => {
      const formVal = (this.generateForm.get(key)?.value || '').toLowerCase();
      const presetVal = (preset.colors[key] || '').toLowerCase();
      return formVal === presetVal;
    });
  }

  appearanceOptionSchema = [
    {
      title: 'Header section',
      formName: 'design_header_section_color',
      subtext: 'Header color',
      selected: '',
      colorOptions: [
        '#45489D',
        '#FFA500',
        '#8DC642',
        '#DC9ED6',
        '#E1C16E',
        '#000000',
        '#ffffff',
      ],
    },
    {
      title: 'Payment button',
      formName: 'design_payment_button_color',
      selected: '',
      colorOptions: [
        '#45489D',
        '#FFA500',
        '#8DC642',
        '#DC9ED6',
        '#E1C16E',
        '#000000',
        '#e3e6e9',
      ],
    },
    {
      title: 'Main title',
      formName: 'design_main_title_color',
      selected: '',
      colorOptions: ['#000000', '#ffffff'],
    },
    {
      title: 'Sub text',
      formName: 'design_sub_text_color',
      selected: '',
      colorOptions: ['#000000', '#ffffff'],
    },
  ];

  constructor(
    private svc: StoreService,
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private router: Router,
    private activeRoute: ActivatedRoute,
    public utils: UtilitiesService,
    private storeInfoService: StoreInfoService,
    @Inject(DOCUMENT) private doc: Document,
    private sanitizer: DomSanitizer,
  ) { }

  private _origHtmlOverflowX = '';
  private _origHtmlOverflowY = '';
  private _origBodyOverflowX = '';
  private _origBodyOverflowY = '';

  ngOnInit(): void {
    this.bearer = localStorage.getItem('bearer');
    this.store_id = localStorage.getItem('store_id');

    // Show the loading state up-front when opening an existing checkout for edit.
    this.isEditLoading = this.activeRoute.snapshot?.url?.[1]?.path === 'edit';

    const html = this.doc.documentElement;
    const body = this.doc.body;
    this._origHtmlOverflowX = html.style.overflowX;
    this._origHtmlOverflowY = html.style.overflowY;
    this._origBodyOverflowX = body.style.overflowX;
    this._origBodyOverflowY = body.style.overflowY;
    html.style.overflowX = 'clip';
    html.style.overflowY = 'visible';
    body.style.overflowX = 'clip';
    body.style.overflowY = 'visible';

    this.buildForm();
    this.fetchCountry();
    this.getStoreInfo();
    this.fetchPaymentConfig();
    this.fetchProductList()
      .then(() => {
        const slug = this.activeRoute.snapshot.params.slug;
        const countryCode = this.activeRoute.snapshot.params.country_code;
        const isEditState = this.activeRoute.snapshot?.url[1]?.path === 'edit';
        this.isEditMode = isEditState;

        if (!isEditState) {
          // Generate New Checkout
          this.updateLink = false;
          this.countryFirstInit = true;
        } else {
          this.fetchExpressCheckoutBySlug(slug, countryCode.toUpperCase())
            .then((response: any) => {
              // Update existing Checkout
              let checkoutData = response?.data?.[0] ?? {};
              let checkoutProducts = checkoutData?.product ?? '';
              let checkoutProductsData = checkoutData?.product_data ?? '';
              checkoutProducts = JSON.parse(checkoutProducts) ?? [];

              this.checkout_link_id = checkoutData?.checkout_link_id;
              this.expressCheckoutData = response?.data ?? [];

              // Update country
              this.changeCountry(checkoutData?.country_code ?? 'MY');
              this.countryFirstInit = true;

              this.changeLanguage(checkoutData);

              if (this.showSubscriptionTab) {
                const isSubscriptionCheckout = checkoutData?.subscription_type &&
                  checkoutData.subscription_type !== 'normal';
                this.changeSubscription(isSubscriptionCheckout ? 'Subscriptions' : 'Products');
              } else {
                this.changeSubscription('Products');
              }

              // Check selected product
              // checkoutProducts.forEach((product) => {
              //   this.insertSelectedProduct(
              //     product.product_id,
              //     product.quantity
              //   );
              // });
              
              // Non-TWTW only: subscription products are saved with cycles. p_id as product_id and sub.id as id
              // subscription list API loads async, so insert normal products
              // immediately and defer subscription products until API resolves
              const hasSubscriptionProduct = 
                !this.isTaiwanLocal && 
                checkoutProducts.some((p: any) => +p.is_subscription === 1);
              
              const normalProducts = hasSubscriptionProduct
                ? checkoutProducts.filter((p: any) => +p.is_subscription !== 1)
                : checkoutProducts;

              normalProducts.forEach((product: any) => {
                this.insertSelectedProduct(product.product_id, product.quantity);
              });

              const finalizeEdit = () => {

              this.updateButtonState();

              this.updateLink = true;

              this.updateFilter();

              // selected color
              this.addSelectedColorInSchema(checkoutData);

              // Restore Product Link toggle + selected domain
              this.restoreProductLink(checkoutData);

              this.isEditLoading = false;
            };
          
            if (hasSubscriptionProduct) {
              const subscriptionProducts = checkoutProducts.filter(
                (p: any) => +p.is_subscription === 1
              );
              Promise.resolve(this.subscriptionApiLoadPromise).then(() => {
                subscriptionProducts.forEach((savedProduct: any) => {
                  const cardPid = this.resolveSubscriptionCardPid(savedProduct);
                  if (cardPid != null) {
                    this.insertSelectedProduct(cardPid, savedProduct.quantity);
                  }
                });
                finalizeEdit();
              });
            } else {
              finalizeEdit();
            }
          })
          .catch((error) => {
            console.log('Loading checkout data error:', { error });
            this.isEditLoading = false;
          });
        }
      })
      .catch((error) => {
        console.log('Loading product error:', { error });
        this.isEditLoading = false;
      });
  }



  ngAfterViewInit(): void {
    const headerEl = this.pageHeader?.nativeElement;
    if (!headerEl || !('IntersectionObserver' in window)) return;

    this.headerObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const next = !entry.isIntersecting;
        if (next !== this.showFloatingCta) {
          this.showFloatingCta = next;
        }
      },
      { threshold: 0 }
    );
    this.headerObserver.observe(headerEl);
  }

  ngOnDestroy(): void {
    this.headerObserver?.disconnect();
    const html = this.doc.documentElement;
    const body = this.doc.body;
    html.style.overflowX = this._origHtmlOverflowX;
    html.style.overflowY = this._origHtmlOverflowY;
    body.style.overflowX = this._origBodyOverflowX;
    body.style.overflowY = this._origBodyOverflowY;
  }

  addSelectedColorInSchema(data) {
    if (data?.option_json !== undefined) {
      let option = JSON.parse(data?.option_json);
      this.appearanceOptionSchema.forEach((schema) => {
        if (option[schema.formName] !== undefined) {
          if (!schema.colorOptions.includes(option[schema.formName])) {
            schema.colorOptions.push(option[schema.formName]);
          }
        }
      });
    }
  }

  async fetchProductList() {
    this.loading = true;

    return new Promise((resolve, reject) => {
      this.svc.getStoreLanguage(this.bearer, this.store_id).subscribe(
        (res) => {
          const allLanguages = res['data'];

          const enabledLanguages = allLanguages.filter((lang) => +lang.status);
          const defaultLanguage =
            allLanguages.find((lang) => +lang.default) ?? allLanguages[0];

          this.languageList = enabledLanguages;

          this.language_code = defaultLanguage['language_code'];
          this.language_name = defaultLanguage['language_name'];

          this.changeLanguage(defaultLanguage);

          this.reloadProductList().finally(() => {
            this.generateArrangementList();
            this.loading = false;

            setTimeout(() => {
              resolve(true);
            }, 1);
          });
        },
        (error) => {
          this.loading = false;
          reject(error);
        }
      );
    });
  }

  async reloadProductList() {
    return new Promise((resolve, reject) => {
      this.svc
        .getProductLists(this.bearer, this.store_id, this.language_code)
        .subscribe(
          async (res) => {
            this.products = res['data'];
            this.loading = false;

            this.subscriptionProduct = this.products.some(
              (p) => +p.is_subscription === 1
            );

            this.changeSubscription(
              this.showSubscriptionTab && this.subscriptionProduct
                ? 'Subscriptions'
                : 'Products'
            );

            this.generateArrangementList(true);

            if (!this.isTaiwanLocal) {
              this.loadSubscriptionApiProducts();
            }

            resolve(true);
          },
          (error) => {
            this.loading = false;
            reject(error);
          }
        );
    });
  }

  loadSubscriptionApiProducts(): Promise<void> {
    this.subscriptionApiLoading = true;
    const countryCode = this.filteredCountry?.toUpperCase();
    this.subscriptionApiLoadPromise = new Promise<void>((resolve) => {
      this.svc.getProductSubscriptionList(this.bearer, this.store_id, countryCode).subscribe(
        (res) => {
          this.subscriptionApiProducts = res['data'] || [];
          this.subscriptionApiLoaded = true;
          this.subscriptionApiLoading = false;
          this.subscriptionProduct = this.subscriptionApiProducts.length > 0;
          resolve();
        },
        () => {
          this.subscriptionApiLoading = false;
          resolve();
        }
      );
    });
    return this.subscriptionApiLoadPromise;
  }

  // Non-TWTW only. Maps a saved subscription product entry back to its card p_id
  // (= sub.id). Handles both the new nested shape
  // ({id, products:[{product_id, quantity}], quantity, is_subscription:1}) and
  // the legacy flat shape ({id, product_id, quantity, is_subscription:1}).
  private resolveSubscriptionCardPid(savedProduct: any): number | null {
    const nestedPid = savedProduct?.products?.[0]?.product_id;
    const legacyPid = savedProduct?.product_id;
    const cycleProductId = nestedPid ?? legacyPid;
    const savedSub = this.subscriptionApiProducts.find(
      (s) =>
        +s.id === +savedProduct.id &&
        +(s.cycles?.[0]?.products?.[0]?.p_id) === +cycleProductId
    );
    return savedSub ? +savedSub.id : null;
  }

  async fetchExpressCheckoutBySlug(slug, countryCode) {
    return new Promise((resolve, reject) => {
      this.svc
        .fetchExpressCheckoutBySlug(
          this.bearer,
          this.store_id,
          slug,
          countryCode
        )
        .subscribe(
          (res) => {
            this.loading = false;
            resolve(res);
          },
          (error) => {
            this.loading = false;
            reject(error);
          }
        );
    });
  }

  saveLastArrangementListChanged(arrangementList = []) {
    this.initialArrangement = arrangementList;
    let countryCode = this.getSelectedCountryCode;
    this.customArrangement[countryCode] = arrangementList;
  }

  saveProductArrangement(event) {
    this.isSavingArrangement = true;
    const body = {
      store_id: this.store_id,
      arrangement: this.productArrangement,
      country_code: this.getSelectedCountryCode,
    };

    this.svc.updateCustomArrangement(body, this.bearer).subscribe(
      (res) => {
        this.isSavingArrangement = false;
        this.saveLastArrangementListChanged(this.productArrangement);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  fetchProductArrangement() {
    this.svc.getProductCustomArrangement(this.bearer).subscribe(
      (res) => {
        this.customArrangement = res?.['data'];
        this.sortByArrangementList();
        this.customArrangementReady = true;
      },
      (error) => {
        console.log(error);
      }
    );
  }

  generateArrangementList(doSort = false) {
    let productArrangement = Object.entries(this.displayedProducts).map(
      (product: any) => +product[1].p_id
    );
    this.productArrangement = productArrangement;
    if (doSort) this.sortByArrangementList();
  }

  sortByArrangementList() {
    this.customArrangement;
    this.productArrangement;

    if (this.customArrangementReady && this.productArrangement?.length) {
      // Arrangement ready
      let countryCode = this.getSelectedCountryCode;
      let newArrangement = [...(this.customArrangement[countryCode] ?? [])];
      this.productArrangement.forEach((p_id) => {
        if (!newArrangement.includes(p_id)) {
          newArrangement.push(p_id);
        }
      });

      this.productArrangement = newArrangement;

      let arrangedProducts = [];
      newArrangement.forEach((p_id) => {
        let productFound = this.displayedProducts.find(
          (product) => +product.p_id === +p_id
        );

        // Only insert non-empty
        if (productFound) arrangedProducts.push(productFound);
      });

      // Replace displayedProducts with arrangedProducts
      this.displayedProducts = arrangedProducts;

      // Save/Reset unchanged arrangement list with new value
      this.saveLastArrangementListChanged(newArrangement);
    }
  }

  get getSelectedCountryCode(): string {
    return this.filteredCountry?.toUpperCase();
  }

  private phoneCodeMap: { [key: string]: string } = {
    MY: '+60',
    SG: '+65',
    HK: '+852',
    MO: '+853',
    TW: '+886',
    TH: '+66',
    ID: '+62',
    PH: '+63',
    VN: '+84',
    AU: '+61',
    NZ: '+64',
    AE: '+971',
    CN: '+86',
    JP: '+81',
    KR: '+82',
    BN: '+673',
    KH: '+855',
    CM: '+855',
    IN: '+91',
    LK: '+94',
    MM: '+95',
    LA: '+856',
    US: '+1',
    GB: '+44',
  };

  getPhoneCode(countryCode: string): string {
    if (!countryCode) return '';
    let key = countryCode.slice(-2).toLowerCase();
    if (key === 'cm') key = 'kh';
    return this.phoneCodeMap[key.toUpperCase()] || '';
  }

  // Taiwan checkout has a distinct preview layout (promotions, payment list, e-invoice).
  // Covers both 'TWTW' (Taiwan) and 'MYTW' (Taiwan Import).
  get isTaiwan(): boolean {
    return this.filteredCountry?.slice(-2).toUpperCase() === 'TW';
  }

  get isTaiwanPreview(): boolean {
    return (this.filteredCountry || '').slice(-2).toUpperCase() === 'TW';
  }

  // Product Link is not offered for the Taiwan (TWTW) store.
  get isTaiwanLocal(): boolean {
    return (this.filteredCountry || '').toUpperCase() === 'TWTW';
  }

  getCountryName(country_code: string) {
    country_code = (country_code || '').toLowerCase();
    const country = this.country?.find(
      (c) => (c.country_code || '').toLowerCase() === country_code
    );
    this.selectedCountry = country_code;
    return country?.country_name ?? country_code;
  }

  getStoreInfo() {
    this.storeInfoService.getStoreInfos().subscribe((res) => {
      this.isSvoStore = res?.config?.is_svo_store;
      this.isStoreInfoLoading = false;
    });
  }

  // Taiwan preview payment methods. Each maps to a payment-preference category
  // returned by estore/config/all (non_cod / cod / installment). Items whose
  // category is turned off in the store preferences are hidden in the preview.
  // Each item lists the preference flags it requires; ALL must be enabled for
  // the item to show. Convenience Store Payment also requires self_collect.
  private twPaymentAll = [
    { label: 'Credit Card (One-time Payment)', icon: 'fi-rr-credit-card', flags: ['non_cod'] },
    { label: 'Credit Card Installment', icon: 'fi-rr-credit-card', flags: ['installment'] },
    { label: 'UnionPay', icon: 'fi-rr-bank', flags: ['non_cod'] },
    { label: 'Mobile Payment', icon: 'fi-rr-mobile-notch', flags: ['non_cod'] },
    { label: 'ATM Transfer', icon: 'fi-rr-bank', flags: ['non_cod'] },
    { label: 'Convenience Store Payment', icon: 'fi-rr-shop', flags: ['non_cod', 'self_collect'] },
    { label: 'Cash on Delivery', icon: 'fi-rr-box', flags: ['cod'] },
    { label: 'Installment', icon: 'fi-rr-calendar', flags: ['installment'] },
  ];
  // Visible list (defaults to all until the config loads, then filtered).
  twPaymentMethods = [...this.twPaymentAll];

  // Set from the store's `checkout_theme` config. The split theme is built
  // around a branded band at the top of the checkout, so the custom header
  // stops being optional there.
  isSplitCheckoutTheme = false;

  // Raw `config/all` payload, kept so the method list can be recomputed when
  // the country changes (payment switches can differ per store country).
  private paymentConfigData: any = null;

  fetchPaymentConfig() {
    this.svc.getAllStoreConfig(this.bearer, this.store_id).subscribe(
      (res) => {
        const data = res?.['data'] ?? {};
        this.paymentConfigData = data;
        this.applyPaymentFlags();

        this.applyCheckoutTheme(data['checkout_theme'] === 'split');
      },
      (error) => {
        // On failure keep the full default list rather than hiding everything.
        console.log('Payment config error:', error);
        this.paymentConfigData = null;
        this.twPaymentMethods = [...this.twPaymentAll];
      }
    );
  }

  // Keeps only the methods whose flags are on for the selected country; before
  // a country is picked the store-wide flags apply.
  applyPaymentFlags() {
    if (!this.paymentConfigData) return;
    const country = this.getSelectedCountryCode;
    this.twPaymentMethods = this.twPaymentAll.filter((method) =>
      method.flags.every((flag) => resolveCountryFlag(this.paymentConfigData, flag, country))
    );
  }

  /**
   * Forces the custom header on and locks the toggle under the split theme.
   *
   * This races the existing-link load, which also writes
   * `custom_header_enabled`; whichever lands second wins, so that path checks
   * `isSplitCheckoutTheme` as well. A disabled control is dropped from
   * `form.value`, so the save payload sets the flag explicitly too.
   */
  private applyCheckoutTheme(isSplit: boolean): void {
    this.isSplitCheckoutTheme = isSplit;

    const control = this.generateForm?.get('custom_header_enabled');
    if (!control) return;

    if (isSplit) {
      control.setValue(true);
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

  // The hand-picked add-ons, for the phone preview. Every pick is listed,
  // out-of-stock ones included and flagged — the dealer is looking at their own
  // configuration here, so silently omitting a pick would just look like a bug.
  // The storefront itself hides out-of-stock add-ons.
  get previewRecommendations() {
    if (!this.productRecommendationsEnabled) return [];
    if (!this.isManualRecommendation) return [];

    return this.productRecommendations
      .map((item) => ({ item, product: this.getProductData(item.product_id) }))
      .filter(({ product }) => product)
      .map(({ item, product }) => ({
        ...product,
        previewQuantity: +item.quantity || 1,
        previewOutOfStock: this.isOutOfStock(product),
      }));
  }

  // Auto mode, or manual with nothing picked: the storefront suggests
  // same-series products, chosen at runtime from the customer's cart. The
  // preview can only say it will happen rather than list them.
  get previewUsesSeriesFallback(): boolean {
    return (
      this.productRecommendationsEnabled &&
      this.previewRecommendations.length === 0
    );
  }

  // Whether the checkout page offers this PWP as the link opens: the link has
  // to start the customer with at least the promotion's minimum.
  pwpMeetsCondition(entry: PwpEntry): boolean {
    return entry.trigger_qty >= entry.min_qty;
  }

  // The PWP rows the checkout page will show as the link opens: every
  // unlocked PWP product the link has not hidden and whose condition the
  // link's own quantities meet, at its pre-selected quantity, with the
  // offered picks of a bundle and how many of them the link already picked.
  // A hidden pick is left out even when picked up front, as the storefront
  // leaves it out.
  get previewPwpRows() {
    return this.getPwpEntries()
      .filter((entry) => this.isPwpVisible(entry) && this.pwpMeetsCondition(entry))
      .map((entry) => {
        const setting = this.getPwpSetting(entry.promotion_id, entry.product_id);
        return {
          ...entry,
          quantity: Math.min(setting.quantity, entry.max_qty),
          locked: setting.locked === true && setting.quantity > 0,
          pick_groups: entry.pick_groups.map((group) => ({
            limit: this.pwpPickLimit(entry, group),
            picked: this.pwpPickCount(entry, group),
            products: group.products
              .filter((product) => this.isPwpPickVisible(entry, product.product_id))
              .map((product) => {
                const pick = this.getPwpPickSetting(entry, product.product_id);
                return {
                  ...product,
                  quantity: pick.quantity,
                  locked: pick.locked === true && pick.quantity > 0,
                };
              }),
          })),
        };
      });
  }

  get phonePreviewTotal(): number {
    const products = this.selectedProducts.reduce((sum, sel) => {
      const p = this.getProductData(sel.product_id);
      if (!p) return sum;
      if (p.isSubscriptionPlan) {
        return sum + (this.getSubscriptionPrice(sel.product_id, sel.quantity) || 0);
      }
      return sum + (+p.retail_price_per_item || 0) * (+sel.quantity || 0);
    }, 0);
    // Pre-selected PWP products are in the order from the start, so they are
    // in the total from the start; most are free, so this is usually 0.
    const pwp = this.previewPwpRows.reduce(
      (sum, row) => sum + (+row.price || 0) * (+row.quantity || 0),
      0
    );
    return products + pwp;
  }

  private get phonePreviewFirstProduct() {
    const sel = this.selectedProducts.find((s) => this.getProductData(s.product_id));
    return sel ? this.getProductData(sel.product_id) : null;
  }

  get phonePreviewCurrency(): string {
    return this.phonePreviewFirstProduct?.currency ?? '';
  }

  get phonePreviewCurrencySymbol(): string {
    return this.phonePreviewFirstProduct?.currency_symbol ?? '';
  }

  // ---- Split checkout theme -----------------------------------------------

  /**
   * Whether the preview should render the split layout.
   *
   * Taiwan keeps the classic checkout whatever the store's theme is set to —
   * the theme picker is hidden there — so it never previews as split.
   */
  get isSplitPreview(): boolean {
    return this.isSplitCheckoutTheme && !this.isTaiwan;
  }

  /**
   * Appearance Settings rows to show.
   *
   * Header section, Main title and Sub text paint the description box and hero
   * that sit above the form on the classic checkout. The split theme renders
   * neither — the customer's own header HTML occupies that space instead — so
   * those three would be controls with nothing to change. Only the payment
   * button colour reaches the split page.
   *
   * Filtered for display only. The values stay in the form and keep saving, so
   * a store that switches back to classic finds its colours as it left them.
   */
  get visibleAppearanceOptions() {
    if (!this.isSplitPreview) return this.appearanceOptionSchema;
    return this.appearanceOptionSchema.filter(
      (option) => option.formName === 'design_payment_button_color'
    );
  }

  private readonly SPLIT_PREVIEW_THUMB_LIMIT = 3;

  /**
   * Thumbnails for the total row above Proceed, matching what the storefront
   * shows there: the first few products, each badged with its quantity.
   */
  get splitPreviewThumbs() {
    return this.selectedProducts
      .slice(0, this.SPLIT_PREVIEW_THUMB_LIMIT)
      .map((sel) => ({
        product: this.getProductData(sel.product_id),
        quantity: +sel.quantity || 1,
      }))
      .filter((entry) => !!entry.product);
  }

  get splitPreviewThumbsOverflow(): number {
    return Math.max(
      0,
      this.selectedProducts.length - this.SPLIT_PREVIEW_THUMB_LIMIT
    );
  }

  fetchCountry() {
    this.svc.getCountry(this.bearer).subscribe((res) => {
      res['data'].forEach((item) => {
        if (item['country_name'] == 'Malaysia-Brunei') {
          item['country_name'] = 'Brunei';
        }
      });
      let defaultCountry = res['data'].find((index) => {
        if (parseInt(index['default']) === 1) {
          return index;
        }
      });

      this.country = [{ country_code: '', country_name: '' }];
      this.filteredCountry = defaultCountry['country_code'];
      this.ensureStorePromotions();
      this.activatedCountry = [];
      this.countryLoaded = true;
      if (res['data']) {
        res['data'].forEach((value) => {
          value.country_name = value.country_name.split('-').pop().trim();
          this.country.push(value);
          if (+value.status === 1) {
            this.activatedCountry.push(value);
          }
        });
      }
    });
  }

  isUpdatingDisplay(productId) {
    return !!this.updatingDisplayQueue.includes(productId);
  }

  addUpdateDisplayQueue(productId) {
    this.updatingDisplayQueue.push(productId);
  }

  removeUpdateDisplayQueue(productId) {
    this.updatingDisplayQueue = this.updatingDisplayQueue.filter(
      (p) => +p !== +productId
    );
  }

  updateDisplayStatus(productId, displayStatus, event) {
    let body = {
      store_id: this.store_id,
      product_id: productId,
      display: +!displayStatus,
      language_code: this.language_code,
    };

    if (this.isUpdatingDisplay(productId)) return;
    this.addUpdateDisplayQueue(productId);

    this.svc.updateDisplayStatus(body, this.bearer).subscribe(
      (res) => {
        let productData = this.getProductData(productId);
        if (productData) productData.display_status = res['data']?.display ? 9 : 1;
        this.removeUpdateDisplayQueue(productId);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  updateFilter(event = null) {
    const filteredProducts = this.filterProducts(this.products, [
      this.filteredString,
      this.filteredCountry,
      this.filteredStatus,
    ]);
    if (this.showSubscriptionTab && this.is_subscription === 1) {
      this.displayedProducts = filteredProducts.filter(
        (product) => +product.is_subscription === 1 && +product.status === 1
      );
    } else {
      this.displayedProducts = filteredProducts.filter(
        (product) => +product.is_subscription === 0
      );
    }
  }

  productFilterChanged(e) {
    this.productFilter = e.target.value;
    this.updateFilter();
  }

  private normalizeSubscription(sub: any) {
    const otpProduct = sub.one_time_purchase?.products?.[0];
    const cycleProduct = sub.cycles?.[0]?.products?.[0];
    const cyclePrice = sub.cycles?.[0]?.price;
    // New schema: price is a qty→price map. Old schema: a single number.
    const priceIsMap = cyclePrice && typeof cyclePrice === 'object';
    const maxQty = priceIsMap
      ? Math.max(...Object.keys(cyclePrice).map((k) => +k).filter((n) => !isNaN(n) && n > 0))
      : +(cycleProduct?.quantity) || 1;
    return {
      p_id: sub.id,
      otp_product_id: otpProduct?.p_id,
      max_qty_per_order: maxQty,
      product_name: sub.title,
      product_display_name: sub.title,
      search_key: sub.title,
      selected_product_name: sub.title || cycleProduct?.product_name,
      country_code: sub.country_code,
      currency: sub.currency,
      currency_symbol: sub.currency,
      retail_price_per_item: priceIsMap ? cyclePrice[1] : cyclePrice,
      image: sub.image || null,
      display_status: 1,
      is_subscription: 1,
      isSubscriptionPlan: true,
      _cyclePrice: cyclePrice,
    };
  }

  get allNormalizedSubscriptionProducts() {
    return this.subscriptionApiProducts.map((sub) => this.normalizeSubscription(sub));
  }

  get normalizedSubscriptionProducts() {
    return this.allNormalizedSubscriptionProducts;
  }

  getSubscriptionSelectedName(productId: number, quantity: number): string {
    const plan = this.getProductData(productId);
    if (!plan?.isSubscriptionPlan) return plan?.product_name || '';
    return plan?.selected_product_name || '';
  }

  getSubscriptionPrice(productId: number, quantity: number): number {
    const plan = this.getProductData(productId);
    if (!plan?.isSubscriptionPlan) return plan?.retail_price_per_item;
    const cyclePrice = plan._cyclePrice;
    if (cyclePrice && typeof cyclePrice === 'object') {
      return +cyclePrice[quantity] || +cyclePrice[1] || 0;
    }
    return +cyclePrice || 0;
  }

  getSubscriptionImage(productId: number, quantity: number): string | null {
    const plan = this.getProductData(productId);
    return plan?.image ?? null;
  }

  getDisplayedProducts() {
    const keywords = this.productFilter.toLowerCase().split(' ');

    if (this.is_subscription === 1 && !this.isTaiwanLocal) {
      return this.normalizedSubscriptionProducts.filter((product) => {
        const title = product.product_name?.toLowerCase() || '';
        return keywords.every((keyword) => title.includes(keyword));
      });
    }

    return this.displayedProducts.filter((product) => {
      if (![1, 6].includes(+product.display_status)) return false;

      const searchKey = product.search_key?.toLowerCase() || '';
      const displayName = product.product_display_name?.toLowerCase() || '';

      return keywords.every(
        (keyword) =>
          searchKey.includes(keyword) || displayName.includes(keyword)
      );
    });
  }

  changeSubscription(sub) {
    if (sub === 'Subscriptions') {
      this.is_subscription = 1;
      this.subscription = 'Subscriptions';
      this.updateFilter();
    } else if (sub === 'Products') {
      this.is_subscription = 0;
      this.subscription = 'Products';
      this.updateFilter();
    }
  }

  filterProducts(products: any, filterString: any) {
    if (!products || products.length === 0) {
      return products;
    }

    return products.filter((product: any) => {
      const keywordsFilter = filterString[0].toLowerCase().split(' ');
      const keywordsFilterResult = keywordsFilter.every(
        (keyword) => product.product_name.toLowerCase().indexOf(keyword) >= 0
      );

      const countryFilter = filterString[1];
      const countryFilterResult =
        countryFilter === '' || product['country_code'] === countryFilter;

      const statusFilter = filterString[2];
      const statusFilterResult =
        statusFilter === '' || product['display_status'] === statusFilter;

      return keywordsFilterResult && countryFilterResult && statusFilterResult;
    });
  }

  changeCountry(countryCode) {
    this.filteredCountry = countryCode;
    this.ensureStorePromotions();
    if (this.isTaiwan && (this.activeCustomTab === 'highlight' || this.activeCustomTab === 'note')) {
      this.activeCustomTab = 'header';
    }
    this.applyPaymentFlags();
    this.selectedProducts = [];
    this.lockedQuantityProductIds = [];
    this.pwpProductSettings = [];
    // Recommendations reference this country's catalogue, so they reset with it.
    this.productRecommendations = [];
    this.productRecommendationFilter = '';
    this.is_subscription = 0;
    this.subscription = 'Products';
    this.subscriptionApiProducts = [];
    this.subscriptionApiLoaded = false;
    this.subscriptionProduct = false;
    if (!this.isTaiwanLocal) {
      this.loadSubscriptionApiProducts();
    }
    this.updateFilter();
    this.generateArrangementList(true);
  }

  changeLanguage(languageData) {
    const { languageList } = this;
    let defaultCheckoutData = this.expressCheckoutData[0] ?? {};

    if (languageData !== undefined) {
      let foundLanguage = languageList.find((language) => {
        return (
          language?.language_code?.toLowerCase() ===
          languageData?.language_code?.toLowerCase()
        );
      });

      languageData = foundLanguage;
      this.currentLanguage = languageData;
      let languageCode = (this.language_code = languageData.language_code);
      let languageName = (this.language_name = languageData.language_name);

      // Find checkout data for the requested language
      let expressCheckoutDataByLanguage = this.expressCheckoutData?.find(
        (linkData) => {
          return linkData.language_code === languageCode;
        }
      );
      this.expressCheckoutDataByLanguage = expressCheckoutDataByLanguage;

      if (!expressCheckoutDataByLanguage) {
        this.checkout_link_id = defaultCheckoutData?.checkout_link_id;
        this.updateLink = false;
      } else {
        this.checkout_link_id = expressCheckoutDataByLanguage?.checkout_link_id;
        this.updateLink = true;
      }

      this.generateForm.get('url').setValue(defaultCheckoutData?.url ?? '');
      this.generateForm
        .get('page_title')
        .setValue(expressCheckoutDataByLanguage?.page_title ?? '');
      this.generateForm
        .get('description')
        .setValue(expressCheckoutDataByLanguage?.description ?? '');

      let optionJson = JSON.parse(
        expressCheckoutDataByLanguage?.option_json ?? '{}'
      );

      // Set Default colors
      this.appearanceOptionSchema.forEach((schema) => {
        this.generateForm
          .get(schema.formName)
          .setValue(optionJson[schema.formName] ?? schema.colorOptions[0]);
      });

      // Under the split theme the header is mandatory, so a link saved before
      // the store switched themes must not restore a stale `false` here.
      this.generateForm
        .get('custom_header_enabled')
        .setValue(
          this.isSplitCheckoutTheme
            ? true
            : optionJson['custom_header_enabled'] ?? false
        );
      this.generateForm
        .get('custom_header_html')
        .setValue(optionJson['custom_header_html'] ?? DEFAULT_CUSTOM_HEADER_HTML);
      const footerHtmlSaved = optionJson['custom_footer_html'];
      const hasSavedFooter = typeof footerHtmlSaved === 'string' && footerHtmlSaved.trim().length > 0;
      this.generateForm
        .get('custom_footer_enabled')
        .setValue(optionJson['custom_footer_enabled'] ?? hasSavedFooter);
      this.generateForm
        .get('custom_footer_html')
        .setValue(hasSavedFooter ? footerHtmlSaved : DEFAULT_CUSTOM_FOOTER_HTML);
      // Off unless the link says otherwise — links saved before Custom
      // Highlight existed carry no flag and must stay off.
      this.generateForm
        .get('custom_highlight_enabled')
        .setValue(optionJson['custom_highlight_enabled'] === true);
      this.generateForm
        .get('custom_highlight_html')
        .setValue(
          optionJson['custom_highlight_html'] ?? DEFAULT_CUSTOM_HIGHLIGHT_HTML
        );
      this.generateForm
        .get('custom_note_enabled')
        .setValue(optionJson['custom_note_enabled'] === true);
      this.generateForm
        .get('custom_note_html')
        .setValue(optionJson['custom_note_html'] ?? DEFAULT_CUSTOM_NOTE_HTML);
      this.generateForm
        .get('product_recommendations')
        .setValue(optionJson['product_recommendations'] ?? true);

      let voucherVal =
        expressCheckoutDataByLanguage?.voucher_status ??
        defaultCheckoutData?.voucher_status ??
        optionJson['voucher_status'];
      this.generateForm
        .get('voucher_status')
        .setValue(
          voucherVal !== undefined && voucherVal !== null
            ? +voucherVal === 1 || voucherVal === true
            : true
        );

      this.restoreLockedQuantityProducts(optionJson);
      this.restorePwpProducts(optionJson);
      this.restoreProductRecommendations(optionJson);
    }
  }

  getLanguageName(languageData) {
    return languageData.language_name;
  }

  open(content, id) {
    this.modalService
      .open(content, {
        ariaLabelledBy: 'modal-basic-title',
        centered: true,
      })
      .result.then(
        (result) => {
          this.closeResult = `Closed with: ${result}`;
          if (result == 'delete') {
            // this.deleteLink(id);
          }
        },
        (reason) => {
          this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
        }
      );
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  get isTileView() {
    return true;
    return this.listingView === 'tile';
  }
  get isListView() {
    return this.listingView === 'list';
  }

  getProductData(productId: number): any {
    const foundProduct =
      this.displayedProducts.find((product) => +product.p_id === +productId) ??
      this.allNormalizedSubscriptionProducts.find((product) => +product.p_id === +productId) ??
      this.products?.find((product) => +product.p_id === +productId);

    if (foundProduct !== undefined && +foundProduct.display_status === 9) {
      return undefined;
    } else {
      return foundProduct;
    }
  }

  getEditProductLink(productId: number) {
    const { language_code } = this;
    return `/dashboard/product/edit/${productId}/${language_code}`;
  }

  getFlagByCountryCode(country_code: string) {
    country_code = (country_code || '').toLowerCase().substr(-2, 2);
    country_code = country_code === 'cm' ? 'kh' : country_code;
    return country_code;
  }

  getFlagImageURL(country_code: string) {
    country_code = this.getFlagByCountryCode(country_code);
    return `https://cdn.estore5s.com/asset/flags/${country_code}.png`;
  }

  productClick(pid) {
    const productAlreadyInCollection = this.selectedProducts.find((product) => {
      return +product.product_id === +pid;
    });

    if (!productAlreadyInCollection) {
      // Out of stock can't be newly added. Deselecting stays possible (the
      // branch below), and insertSelectedProduct still accepts one, so editing
      // a link whose product has since sold out doesn't lose it.
      if (this.isOutOfStock(this.getProductData(pid))) {
        this.utils.notify.showWarning(
          this.utils.translate(
            'This product is out of stock and cannot be selected.'
          )
        );
        return;
      }

      // Subscription 2.0: restrict to one product per product_group_name
      if (this.is_subscription === 1) {
        const newProductData = this.products.find((p) => +p.p_id === +pid);
        const conflictingSelected = this.selectedProducts.find((selected) => {
          const selectedData = this.products.find(
            (p) => +p.p_id === +selected.product_id
          );
          return (
            selectedData?.product_group_name &&
            newProductData?.product_group_name &&
            selectedData.product_group_name === newProductData.product_group_name
          );
        });

        if (conflictingSelected) {
          this.utils.notify.showWarning(
            this.utils.translate(
              'Only one {0} product can be selected at a time. Please deselect the existing one first.',
              [newProductData.product_group_name]
            )
          );
          return;
        }
      }

      this.insertSelectedProduct(pid);
    } else {
      this.removeSelectedProduct(pid);
    }
  }

  insertSelectedProduct(pid, qty = null) {
    const product = this.getProductData(pid);

    if (product === undefined) {
      this.unavailableProducts.push({
        product_id: +pid,
      });
    }

    const resolvedQty = qty ?? product?.default_quantity ?? 1;
    const isSubPlan = !!product?.isSubscriptionPlan;
    this.selectedProducts.push({
      product_id: +pid,
      // Subscription 2.0: cap at 1 for TWTW subscription products; subscription plans use max_qty_per_order
      quantity: (this.is_subscription === 1 && !isSubPlan) ? Math.min(resolvedQty, 1) : resolvedQty,
    });
    // A product sold by the link can't also be recommended as an add-on.
    this.removeRecommendation(pid);
    this.body['product'] = this.selectedProducts;
  }
  // `row` lets the unavailable-product rows remove themselves by reference: a
  // saved entry with no usable product_id resolves to NaN, and NaN never
  // compares equal, so an id filter would leave it in place.
  removeSelectedProduct(pid, row?) {
    this.selectedProducts = this.selectedProducts.filter((product) =>
      row ? product !== row : +product.product_id !== +pid
    );

    // Rebuilt from what is still selected, mirroring the template's
    // "no longer available" check, so NaN ids can't get stuck here either.
    this.unavailableProducts = this.selectedProducts
      .filter((product) => !this.getProductData(product.product_id))
      .map((product) => ({ product_id: +product.product_id }));

    // A product that is no longer sold by the link has nothing to lock.
    this.removeQuantityLock(pid);

    this.updateButtonState();

    this.body['product'] = this.selectedProducts;
  }

  isProductSelected(pid) {
    return this.selectedProducts.find(
      (product) => +product.product_id === +pid
    );
  }

  // ---- Quantity Lock ------------------------------------------------------

  // The one gate for the whole feature. TWTW keeps the legacy single-page
  // checkout and does not offer Quantity Lock, so a stale list on a link saved
  // elsewhere and moved there stays inert — in the settings card and in the
  // phone preview alike.
  isQuantityLocked(pid): boolean {
    if (this.isTaiwanLocal) return false;
    return this.lockedQuantityProductIds.includes(+pid);
  }

  toggleQuantityLock(pid) {
    const locked = !this.isQuantityLocked(pid);
    this.removeQuantityLock(pid);
    if (locked) this.lockedQuantityProductIds.push(+pid);
  }

  private removeQuantityLock(pid) {
    this.lockedQuantityProductIds = this.lockedQuantityProductIds.filter(
      (id) => id !== +pid
    );
  }

  // option_json payload shape: the ids of the products the link actually
  // sells, so deselecting a product can never leave a lock behind for a
  // product the customer never sees. Empty for TWTW, which does not offer the
  // setting.
  private buildLockedQuantityList(): number[] {
    if (this.isTaiwanLocal) return [];

    return this.selectedProducts
      .map((product) => +product.product_id)
      .filter((pid) => this.lockedQuantityProductIds.includes(pid));
  }

  // Rebuild the list from a saved option_json. Runs before the products load
  // on the edit boot, and again on every language switch — hence the plain
  // assignment rather than a merge.
  private restoreLockedQuantityProducts(optionJson) {
    const raw = optionJson?.['locked_quantity_products'];
    this.lockedQuantityProductIds = (Array.isArray(raw) ? raw : [])
      .map((pid) => +pid)
      .filter((pid) => !isNaN(pid) && pid > 0);
  }

  // ---- PWP (Purchase With Purchase) --------------------------------------

  // The promotions are asked for once, the first time a country that offers
  // the card is active. The PWP card is not offered on TWTW, so a Taiwan
  // store never requests them — the whole feature stays inert there.
  private promotionsRequested = false;

  private ensureStorePromotions() {
    if (this.isTaiwanLocal || this.promotionsRequested) return;
    this.promotionsRequested = true;
    this.fetchStorePromotions();
  }

  // Every active, in-date promotion, whichever side of the store's promotion
  // config it sits on: the checkout page applies them all, so the card must
  // know them all. Two calls because /promotion/list splits the list on that
  // config and offers no way to ask for both sides at once.
  fetchStorePromotions() {
    forkJoin([
      this.svc.getActivePromotions(this.store_id, this.bearer, false),
      this.svc.getActivePromotions(this.store_id, this.bearer, true),
    ]).subscribe(
      ([remaining, inStore]) => {
        const seen = new Set<number>();
        this.storePromotions = [
          ...(Object.values(remaining?.['data'] ?? {}) as any[]),
          ...(Object.values(inStore?.['data'] ?? {}) as any[]),
        ].filter((promo) => {
          const id = +promo?.promotion_id;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      },
      (error) => {
        console.log('Loading promotions error:', { error });
        this.storePromotions = [];
      }
    );
  }

  private parseRequirement(promo): any[] {
    const raw = promo?.requirement;
    if (Array.isArray(raw)) return raw;
    try {
      const parsed = JSON.parse(raw ?? '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  // The promotions a product unlocks: those that name it in promo_product (or
  // fixed_p_id) for its country. Promotions keyed on app_code apply to the
  // whole catalogue and are nobody's PWP in particular, so they stay out.
  private getPwpPromotionsFor(product): any[] {
    const pid = +product?.p_id;
    const country = (product?.country_code ?? '').toUpperCase();

    return this.storePromotions.filter((promo) => {
      if ((promo?.country ?? '').toUpperCase() !== country) return false;

      return this.parseRequirement(promo).some((req) => {
        const fixed = req?.fixed_p_id;
        const ids = [
          ...(Array.isArray(req?.promo_product?.p_id) ? req.promo_product.p_id : []),
          ...(Array.isArray(fixed) ? fixed : fixed ? [fixed] : []),
        ];
        return ids.some((id) => +id === pid);
      });
    });
  }

  // The quantity of a product a promotion asks for before it applies: the
  // largest qty_min among the requirements that name the product. 1 when the
  // promotion sets none.
  private getPwpMinQty(promo, pid: number): number {
    return this.parseRequirement(promo).reduce((min, req) => {
      const ids = Array.isArray(req?.promo_product?.p_id) ? req.promo_product.p_id : [];
      if (!ids.some((id) => +id === pid)) return min;
      return Math.max(min, Math.floor(+req?.promo_condition?.qty_min) || 1);
    }, 1);
  }

  // The rows of the PWP card: one per promotion product the selected products
  // unlock, each with the products that unlock it. Read by the template, so
  // kept cheap — a few selected products against a few promotions.
  getPwpEntries(): PwpEntry[] {
    if (this.isTaiwanLocal || !this.storePromotions.length) return [];

    const entries: PwpEntry[] = [];

    this.selectedProducts.forEach((selected) => {
      const trigger = this.getProductData(selected.product_id);
      if (!trigger) return;

      this.getPwpPromotionsFor(trigger).forEach((promo) => {
        (promo?.promotion_action_product ?? []).forEach((action) => {
          const productId = +action?.p_id;
          if (!productId) return;

          const existing = entries.find(
            (entry) =>
              entry.promotion_id === +promo.promotion_id &&
              entry.product_id === productId
          );
          if (existing) {
            if (!existing.trigger_names.includes(trigger.product_name)) {
              existing.trigger_names.push(trigger.product_name);
              existing.trigger_qty += Math.max(+selected.quantity || 0, 0);
            }
            existing.min_qty = Math.max(existing.min_qty, this.getPwpMinQty(promo, +trigger.p_id));
            return;
          }

          // The action product is usually in the catalogue too (hidden, as
          // display_status 9), which gives a price and a localised name; the
          // promotion's own copy is the fallback.
          const product = this.products.find((p) => +p.p_id === productId);
          entries.push({
            promotion_id: +promo.promotion_id,
            promotion_name: promo.promotion_name ?? '',
            product_id: productId,
            product_name: product?.product_name ?? action?.product_name ?? '',
            image: product?.image ?? action?.images ?? '',
            price: +(product?.retail_price_per_item ?? 0),
            currency: product?.currency ?? trigger?.currency,
            currency_symbol: product?.currency_symbol ?? trigger?.currency_symbol,
            max_qty: Math.max(+action?.qty || 1, 1),
            trigger_names: [trigger.product_name],
            min_qty: this.getPwpMinQty(promo, +trigger.p_id),
            trigger_qty: Math.max(+selected.quantity || 0, 0),
            pick_groups: this.buildPwpPickGroups(product),
          });
        });
      });
    });

    return entries;
  }

  hasPwpEntries(): boolean {
    return this.getPwpEntries().length > 0;
  }

  // getPwpEntries builds fresh objects on every change-detection pass, so the
  // rows are tracked by their key or the stepper input would be rebuilt (and
  // lose focus) each time.
  trackPwpEntry(index: number, entry: PwpEntry): string {
    return `${entry.promotion_id}:${entry.product_id}`;
  }

  // Defaults match the checkout page's own behaviour for a link that says
  // nothing: the row is shown and nothing is pre-selected.
  getPwpSetting(promotionId: number, productId: number): PwpProductSetting {
    return (
      this.pwpProductSettings.find(
        (setting) =>
          setting.promotion_id === +promotionId &&
          setting.product_id === +productId
      ) ?? {
        promotion_id: +promotionId,
        product_id: +productId,
        quantity: 0,
        visible: true,
        locked: false,
        picks: [],
      }
    );
  }

  isPwpLocked(entry: PwpEntry): boolean {
    return this.getPwpSetting(entry.promotion_id, entry.product_id).locked === true;
  }

  togglePwpLocked(entry: PwpEntry) {
    this.upsertPwpSetting({
      ...this.getPwpSetting(entry.promotion_id, entry.product_id),
      locked: !this.isPwpLocked(entry),
    });
  }

  // ---- PWP pick products ---------------------------------------------------

  // The selectable groups of a PWP bundle, from the catalogue's mixture_arr.
  // A group with one product is fixed by the promotion itself, so only groups
  // the customer actually chooses from get settings.
  private buildPwpPickGroups(product): PwpPickGroup[] {
    const groups = new Map<string, PwpPickGroup>();
    (product?.mixture_arr ?? []).forEach((item) => {
      const productId = +item?.p_id;
      if (!productId) return;
      const key = String(item?.mixture_group ?? 1);
      if (!groups.has(key)) {
        groups.set(key, {
          group: key,
          per_unit: Math.max(+item?.mixture_quantity || 1, 1),
          products: [],
        });
      }
      groups.get(key).products.push({
        product_id: productId,
        product_name: item?.product_name ?? item?.product_display_name ?? '',
        image: item?.image ?? '',
      });
    });
    return [...groups.values()].filter((group) => group.products.length > 1);
  }

  getPwpPickSetting(entry: PwpEntry, pickId: number): PwpPickSetting {
    return (
      this.getPwpSetting(entry.promotion_id, entry.product_id).picks.find(
        (pick) => pick.product_id === +pickId
      ) ?? { product_id: +pickId, quantity: 0, visible: true, locked: false }
    );
  }

  isPwpPickLocked(entry: PwpEntry, pickId: number): boolean {
    return this.getPwpPickSetting(entry, pickId).locked === true;
  }

  togglePwpPickLocked(entry: PwpEntry, pickId: number) {
    this.upsertPwpPick(entry, {
      ...this.getPwpPickSetting(entry, pickId),
      locked: !this.isPwpPickLocked(entry, pickId),
    });
  }

  // The pick groups are rebuilt with their entry on every change-detection
  // pass too. Untracked, Angular replaced each group's DOM on every pass — so
  // a click on a pick's show/hide label toggled a checkbox that had already
  // been torn down, whose listener was gone, and nothing changed.
  trackPwpGroup(index: number, group: PwpPickGroup): string {
    return group.group;
  }

  trackPwpPick(index: number, pick: { product_id: number }): number {
    return pick.product_id;
  }

  // How many the customer picks from a group: the group's per-unit count times
  // the PWP quantity the link pre-selects. 0 until a quantity is set.
  pwpPickLimit(entry: PwpEntry, group: PwpPickGroup): number {
    return group.per_unit * this.getPwpSetting(entry.promotion_id, entry.product_id).quantity;
  }

  pwpPickCount(entry: PwpEntry, group: PwpPickGroup): number {
    return group.products.reduce(
      (sum, product) => sum + this.getPwpPickSetting(entry, product.product_id).quantity,
      0
    );
  }

  private upsertPwpPick(entry: PwpEntry, pick: PwpPickSetting) {
    const setting = this.getPwpSetting(entry.promotion_id, entry.product_id);
    this.upsertPwpSetting({
      ...setting,
      picks: [
        ...setting.picks.filter((item) => item.product_id !== pick.product_id),
        pick,
      ],
    });
  }

  // Clamped so the group never holds more than the customer would be asked
  // to pick; the other picks in the group keep what they have.
  setPwpPickQty(entry: PwpEntry, group: PwpPickGroup, pickId: number, quantity: number) {
    const current = this.getPwpPickSetting(entry, pickId);
    const room = this.pwpPickLimit(entry, group) - (this.pwpPickCount(entry, group) - current.quantity);
    const qty = Math.min(Math.max(Math.floor(+quantity) || 0, 0), Math.max(room, 0));
    this.upsertPwpPick(entry, { ...current, quantity: qty });
  }

  incPwpPickQty(entry: PwpEntry, group: PwpPickGroup, pickId: number) {
    this.setPwpPickQty(entry, group, pickId, this.getPwpPickSetting(entry, pickId).quantity + 1);
  }

  decPwpPickQty(entry: PwpEntry, group: PwpPickGroup, pickId: number) {
    this.setPwpPickQty(entry, group, pickId, this.getPwpPickSetting(entry, pickId).quantity - 1);
  }

  setExactPwpPickQty(entry: PwpEntry, group: PwpPickGroup, pickId: number, event) {
    this.setPwpPickQty(entry, group, pickId, +event?.target?.value);
    if (event?.target) {
      event.target.value = this.getPwpPickSetting(entry, pickId).quantity;
    }
  }

  isPwpPickVisible(entry: PwpEntry, pickId: number): boolean {
    return this.getPwpPickSetting(entry, pickId).visible !== false;
  }

  togglePwpPickVisible(entry: PwpEntry, pickId: number) {
    this.upsertPwpPick(entry, {
      ...this.getPwpPickSetting(entry, pickId),
      visible: !this.isPwpPickVisible(entry, pickId),
    });
  }

  // The picks saved for an entry: only products the bundle still offers, each
  // group trimmed to its limit so a lowered PWP quantity cannot leave more
  // picks than the customer would be asked for.
  private buildPwpPickList(entry: PwpEntry): PwpPickSetting[] {
    const picks: PwpPickSetting[] = [];
    entry.pick_groups.forEach((group) => {
      let room = this.pwpPickLimit(entry, group);
      group.products.forEach((product) => {
        const pick = this.getPwpPickSetting(entry, product.product_id);
        const quantity = Math.min(pick.quantity, Math.max(room, 0));
        room -= quantity;
        if (quantity > 0 || pick.visible === false) {
          picks.push({
            product_id: product.product_id,
            quantity,
            visible: pick.visible !== false,
            locked: pick.locked === true,
          });
        }
      });
    });
    return picks;
  }

  private normalizePwpPicks(raw): PwpPickSetting[] {
    return (Array.isArray(raw) ? raw : [])
      .map((item) => ({
        product_id: +item?.product_id,
        quantity: Math.max(Math.floor(+item?.quantity) || 0, 0),
        visible: this.readVisible(item?.visible),
        locked: this.readLocked(item?.locked),
      }))
      .filter((item) => item.product_id > 0);
  }

  private readVisible(value): boolean {
    return value !== false && value !== 'false' && value !== 0 && value !== '0';
  }

  private readLocked(value): boolean {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  private upsertPwpSetting(setting: PwpProductSetting) {
    this.pwpProductSettings = [
      ...this.pwpProductSettings.filter(
        (item) =>
          !(
            item.promotion_id === setting.promotion_id &&
            item.product_id === setting.product_id
          )
      ),
      setting,
    ];
  }

  setPwpQty(entry: PwpEntry, quantity: number) {
    const qty = Math.min(
      Math.max(Math.floor(+quantity) || 0, 0),
      entry.max_qty
    );
    const setting = this.getPwpSetting(entry.promotion_id, entry.product_id);
    this.upsertPwpSetting({
      ...setting,
      quantity: qty,
      picks: this.trimPwpPicks(entry, setting.picks, qty),
    });
  }

  // A lowered PWP quantity shrinks every pick group's limit, so the picks are
  // cut back to it right away (not only on save): options keep their
  // quantity in list order and the excess comes off the last ones, the same
  // rule buildPwpPickList applies to the saved payload.
  private trimPwpPicks(entry: PwpEntry, picks: PwpPickSetting[], quantity: number): PwpPickSetting[] {
    const trimmed = picks.map((pick) => ({ ...pick }));
    entry.pick_groups.forEach((group) => {
      let room = group.per_unit * quantity;
      group.products.forEach((product) => {
        const pick = trimmed.find((item) => item.product_id === +product.product_id);
        if (!pick) return;
        pick.quantity = Math.min(pick.quantity, Math.max(room, 0));
        room -= pick.quantity;
      });
    });
    return trimmed;
  }

  incPwpQty(entry: PwpEntry) {
    this.setPwpQty(
      entry,
      this.getPwpSetting(entry.promotion_id, entry.product_id).quantity + 1
    );
  }

  decPwpQty(entry: PwpEntry) {
    this.setPwpQty(
      entry,
      this.getPwpSetting(entry.promotion_id, entry.product_id).quantity - 1
    );
  }

  setExactPwpQty(entry: PwpEntry, event) {
    this.setPwpQty(entry, +event?.target?.value);
    // Echo the clamped value back so the box never shows an out-of-range number.
    if (event?.target) {
      event.target.value = this.getPwpSetting(
        entry.promotion_id,
        entry.product_id
      ).quantity;
    }
  }

  isPwpVisible(entry: PwpEntry): boolean {
    return this.getPwpSetting(entry.promotion_id, entry.product_id).visible !== false;
  }

  togglePwpVisible(entry: PwpEntry) {
    this.upsertPwpSetting({
      ...this.getPwpSetting(entry.promotion_id, entry.product_id),
      visible: !this.isPwpVisible(entry),
    });
  }

  // option_json payload shape: one entry per PWP product the link currently
  // unlocks, so deselecting a product never leaves a setting behind for a
  // promotion the customer can no longer reach. Empty for TWTW, which does not
  // offer the card. Until the promotions have loaded (or if they failed to)
  // the restored list is sent back untouched rather than wiped.
  private buildPwpProductList(): PwpProductSetting[] {
    if (this.isTaiwanLocal) return [];
    if (!this.storePromotions.length) return this.pwpProductSettings;

    return this.getPwpEntries().map((entry) => {
      const setting = this.getPwpSetting(entry.promotion_id, entry.product_id);
      return {
        promotion_id: entry.promotion_id,
        product_id: entry.product_id,
        quantity: Math.min(setting.quantity, entry.max_qty),
        visible: setting.visible !== false,
        locked: setting.locked === true,
        picks: this.buildPwpPickList(entry),
      };
    });
  }

  // Rebuild the list from a saved option_json. Runs before the promotions and
  // products load on the edit boot, and again on every language switch —
  // hence the plain assignment rather than a merge.
  private restorePwpProducts(optionJson) {
    const raw = optionJson?.['pwp_products'];
    this.pwpProductSettings = (Array.isArray(raw) ? raw : [])
      .map((item) => ({
        promotion_id: +item?.promotion_id,
        product_id: +item?.product_id,
        quantity: Math.max(Math.floor(+item?.quantity) || 0, 0),
        visible: this.readVisible(item?.visible),
        locked: this.readLocked(item?.locked),
        picks: this.normalizePwpPicks(item?.picks),
      }))
      .filter((item) => item.promotion_id > 0 && item.product_id > 0);
  }

  updateButtonState() {
    this.buttonFreeze = this.unavailableProducts.length > 0;
  }

  incQty(pid) {
    this.updateQty(pid, 1, true);
  }

  decQty(pid) {
    this.updateQty(pid, -1, true);
  }

  MIN_CUSTOM_QUANTITY = 0;
  updateQty(pid, value: number = 1, addition: boolean = false) {
    console.log({ pid, value, addition });
    value = +value;
    if (isNaN(value)) value = this.MIN_CUSTOM_QUANTITY;

    this.selectedProducts.forEach((product) => {
      if (+product.product_id === +pid) {
        if (addition) {
          product.quantity += value;
        } else {
          product.quantity = value;
        }

        if (product.quantity < this.MIN_CUSTOM_QUANTITY) {
          product.quantity = this.MIN_CUSTOM_QUANTITY;
          this.utils.notify.showInfo(
            this.utils.translate(`Quantity cannot be less than {0}`, [
              this.MIN_CUSTOM_QUANTITY,
            ])
          );
        }

        // Subscription 2.0: cap at 1 for TWTW subscription products only
        if (this.is_subscription === 1 && product.quantity > 1 && !this.getProductData(pid)?.isSubscriptionPlan) {
          product.quantity = 1;
          this.utils.notify.showWarning(
            this.utils.translate(`Subscription product quantity cannot exceed 1`)
          );
          return;
        }

        const productData = this.getProductData(pid);
        const maxQtyOrder: number = productData ? +(productData.max_qty_per_order) : NaN;
        if (!isNaN(maxQtyOrder) && product.quantity > maxQtyOrder) {
          product.quantity = maxQtyOrder;
          this.utils.notify.showWarning(
            this.utils.translate(
              `Cannot exceed max order quantity for this product (Max: {0})`,
              [maxQtyOrder]
            )
          );
        }
      }
    });

    this.body['product'] = this.selectedProducts;
  }

  setExactQty(pid, event) {
    let value = +event.target.value;

    if (isNaN(value)) {
      value = this.MIN_CUSTOM_QUANTITY;
      event.target.value = value;
    }

    this.updateQty(pid, value);
  }

  /* Product Recommendations -------------------------------------------------
     Add-on products the checkout page offers alongside the link's own cart.
     Two modes, persisted in `option_json.product_recommendations_mode`:

       'auto'   — the storefront suggests products from the same series as the
                  cart items. No list to configure.
       'manual' — the dealer hand-picks them. Only products NOT in
                  `selectedProducts` are eligible, and at most
                  MAX_PRODUCT_RECOMMENDATIONS of them; saved to
                  `option_json.product_recommendations_list` as
                  [{ product_id, quantity }].

     Not offered for the Taiwan (TWTW) store. */

  readonly RECOMMENDATION_MODE_AUTO = RECOMMENDATION_MODE_AUTO;
  readonly RECOMMENDATION_MODE_MANUAL = RECOMMENDATION_MODE_MANUAL;

  get productRecommendationsEnabled(): boolean {
    return !!this.generateForm?.get('product_recommendations')?.value;
  }

  get productRecommendationMode(): string {
    return (
      this.generateForm?.get('product_recommendations_mode')?.value ??
      RECOMMENDATION_MODE_AUTO
    );
  }

  // Only 'manual' has a list to show / save.
  get isManualRecommendation(): boolean {
    return this.productRecommendationMode === RECOMMENDATION_MODE_MANUAL;
  }

  // The Select Products catalogue minus whatever the link already sells,
  // narrowed by this card's own search box. Shares `displayedProducts`, so it
  // follows the same country and Products/Subscriptions tab as the grid above —
  // a subscription link recommends subscription products, and vice versa.
  getRecommendationCandidates() {
    const keywords = this.productRecommendationFilter.toLowerCase().split(' ');

    return this.displayedProducts.filter((product) => {
      if (![1, 6].includes(+product.display_status)) return false;
      if (this.isProductSelected(product.p_id)) return false;

      const searchKey = product.search_key?.toLowerCase() || '';
      const displayName = product.product_display_name?.toLowerCase() || '';

      return keywords.every(
        (keyword) =>
          searchKey.includes(keyword) || displayName.includes(keyword)
      );
    });
  }

  recommendationFilterChanged(e) {
    this.productRecommendationFilter = e.target.value;
  }

  // display_status 6 = out of stock. Still pickable — stock moves and links are
  // long-lived — but the checkout page renders it as unavailable, so the picker
  // says so up front.
  isOutOfStock(product): boolean {
    return +product?.display_status === 6;
  }

  isProductRecommended(pid) {
    return this.productRecommendations.find(
      (product) => +product.product_id === +pid
    );
  }

  recommendationClick(pid) {
    // Deselecting always works — including for a product that has gone out of
    // stock since it was picked, which is the only way to get rid of it.
    if (this.isProductRecommended(pid)) {
      this.removeRecommendation(pid);
      return;
    }

    // The checkout page hides out-of-stock add-ons, so picking one here would
    // silently do nothing. Blocked at the click rather than in
    // insertRecommendation, which still has to accept a saved product that has
    // since gone out of stock.
    if (this.isOutOfStock(this.getProductData(pid))) {
      this.utils.notify.showWarning(
        this.utils.translate(
          'This product is out of stock and cannot be recommended.'
        )
      );
      return;
    }

    if (
      this.productRecommendations.length >= this.MAX_PRODUCT_RECOMMENDATIONS
    ) {
      this.utils.notify.showWarning(
        this.utils.translate(
          'You can recommend up to {0} products only. Remove one to add another.',
          [this.MAX_PRODUCT_RECOMMENDATIONS]
        )
      );
      return;
    }

    this.insertRecommendation(pid);
  }

  insertRecommendation(pid, qty = null) {
    if (this.isProductRecommended(pid)) return;
    if (this.productRecommendations.length >= this.MAX_PRODUCT_RECOMMENDATIONS)
      return;

    // Unlike the cart list, an unresolvable product is dropped rather than
    // flagged: a recommendation is an optional extra and must never block the
    // link from being saved.
    const product = this.getProductData(pid);
    if (product === undefined) return;

    this.productRecommendations.push({
      product_id: +pid,
      quantity: Math.max(+(qty ?? product?.default_quantity ?? 1) || 1, 1),
    });
  }

  removeRecommendation(pid) {
    this.productRecommendations = this.productRecommendations.filter(
      (product) => +product.product_id !== +pid
    );
  }

  incRecommendationQty(pid) {
    this.updateRecommendationQty(pid, 1, true);
  }

  decRecommendationQty(pid) {
    this.updateRecommendationQty(pid, -1, true);
  }

  updateRecommendationQty(pid, value: number = 1, addition: boolean = false) {
    value = +value;
    if (isNaN(value)) value = 1;

    this.productRecommendations.forEach((product) => {
      if (+product.product_id !== +pid) return;

      product.quantity = addition ? product.quantity + value : value;

      // An add-on suggestion of 0 is meaningless, so the floor here is 1 —
      // not MIN_CUSTOM_QUANTITY like the cart list.
      if (product.quantity < 1) {
        product.quantity = 1;
        this.utils.notify.showInfo(
          this.utils.translate('Quantity cannot be less than {0}', [1])
        );
      }

      const maxQty = +this.getProductData(pid)?.max_qty_per_order;
      if (!isNaN(maxQty) && maxQty > 0 && product.quantity > maxQty) {
        product.quantity = maxQty;
        this.utils.notify.showWarning(
          this.utils.translate(
            'Cannot exceed max order quantity for this product (Max: {0})',
            [maxQty]
          )
        );
      }
    });
  }

  setExactRecommendationQty(pid, event) {
    let value = +event.target.value;

    if (isNaN(value)) {
      value = 1;
      event.target.value = value;
    }

    this.updateRecommendationQty(pid, value);
  }

  // option_json payload shape. Empty whenever the feature is switched off or
  // left on auto, so flipping either back never silently resurrects a stale
  // list — and an empty list is exactly what tells the storefront to fall back
  // to same-series suggestions.
  private buildRecommendationList() {
    if (!this.productRecommendationsEnabled) return [];
    if (!this.isManualRecommendation) return [];

    return this.productRecommendations
      .filter((item) => this.getProductData(item.product_id))
      // Final guard on the disjointness invariant — the payload is the one
      // place it must always hold.
      .filter((item) => !this.isProductSelected(item.product_id))
      .slice(0, this.MAX_PRODUCT_RECOMMENDATIONS)
      .map((item) => ({
        product_id: +item.product_id,
        quantity: Math.max(+item.quantity || 1, 1),
      }));
  }

  // Rebuild the list from a saved option_json. Goes through
  // insertRecommendation so the cap and the resolvable-product rule apply on
  // load too.
  //
  // Also runs on every language switch, where `selectedProducts` is already
  // populated — hence the explicit disjointness check. On the edit boot the
  // check is a no-op (products load after this) and insertSelectedProduct
  // enforces it instead.
  private restoreProductRecommendations(optionJson) {
    this.productRecommendations = [];
    this.productRecommendationFilter = '';

    const list = optionJson?.['product_recommendations_list'];

    // Links saved before the mode existed carry no `..._mode`: a list means the
    // dealer hand-picked, no list means the old same-series behaviour.
    const savedMode = optionJson?.['product_recommendations_mode'];
    const mode =
      savedMode === RECOMMENDATION_MODE_MANUAL ||
        savedMode === RECOMMENDATION_MODE_AUTO
        ? savedMode
        : Array.isArray(list) && list.length
          ? RECOMMENDATION_MODE_MANUAL
          : RECOMMENDATION_MODE_AUTO;

    this.generateForm.get('product_recommendations_mode').setValue(mode);

    if (!Array.isArray(list)) return;

    list.forEach((item) => {
      const pid = +item?.product_id;
      if (!pid) return;
      if (this.isProductSelected(pid)) return;
      this.insertRecommendation(pid, +item?.quantity || 1);
    });
  }

  generatelink() {
    if (!this.selectedProducts.length) {
      this.utils.msgBox.open({
        title: this.utils.translate('Uh oh!'),
        icon: 'warning',
        message: this.utils.translate('Please select at least 1 product.'),
      });
      return;
    }

    let languageCode = this.currentLanguage['language_code'];

    this.page_title = this.generateForm.get('page_title').value;
    this.url = this.generateForm.get('url').value;
    this.description = this.generateForm.get('description').value;

    this.body['store_id'] = this.store_id;
    this.body['country_code'] = this.selectedCountry.toUpperCase();
    this.body['page_title'] = this.page_title;
    this.body['url'] = this.url;
    this.body['description'] = this.description;
    this.body['language_code'] = languageCode;

    this.body['product'] = this.checkProductList(this.selectedProducts);

    let optionJson = {};
    this.appearanceOptionSchema.forEach((schema) => {
      optionJson[schema.formName] =
        this.generateForm.get(schema.formName).value ?? schema.colorOptions[0];
    });
    // Forced on under the split theme: the control is disabled there, and a
    // disabled control is excluded from form.value, so read it explicitly.
    optionJson['custom_header_enabled'] = this.isSplitCheckoutTheme
      ? true
      : this.generateForm.get('custom_header_enabled').value ?? false;
    optionJson['custom_header_html'] =
      this.generateForm.get('custom_header_html').value ?? DEFAULT_CUSTOM_HEADER_HTML;
    // The storefronts render custom_footer_html whenever it's non-empty and
    // don't read the enabled flag, so a disabled footer must save as blank —
    // otherwise the sample footer would go live on the checkout page.
    optionJson['custom_footer_enabled'] =
      this.generateForm.get('custom_footer_enabled').value === true;
    optionJson['custom_footer_html'] = optionJson['custom_footer_enabled']
      ? this.generateForm.get('custom_footer_html').value ?? ''
      : '';
    // Custom Highlight — the HTML block shown above the voucher field. The flag
    // is always sent so switching the toggle back off actually takes the block
    // off the page, and the markup goes with it either way so a dealer who
    // toggles off does not lose what they wrote. Forced off for both Taiwan
    // stores (TWTW and MYTW), matching how the card is hidden in the UI.
    optionJson['custom_highlight_enabled'] = this.isTaiwan
      ? false
      : this.generateForm.get('custom_highlight_enabled').value === true;
    optionJson['custom_highlight_html'] =
      this.generateForm.get('custom_highlight_html').value ??
      DEFAULT_CUSTOM_HIGHLIGHT_HTML;
    // Custom Note — the HTML block shown under the order total. Same handling
    // as Custom Highlight above, including being forced off for Taiwan.
    optionJson['custom_note_enabled'] = this.isTaiwan
      ? false
      : this.generateForm.get('custom_note_enabled').value === true;
    optionJson['custom_note_html'] =
      this.generateForm.get('custom_note_html').value ?? DEFAULT_CUSTOM_NOTE_HTML;
    // Product Recommendations is not offered for the Taiwan (TWTW) store — force
    // it off in the payload there, matching how the card is hidden in the UI.
    optionJson['product_recommendations'] = this.isTaiwanLocal
      ? false
      : (this.generateForm.get('product_recommendations').value ?? true);
    optionJson['product_recommendations_mode'] = this.isTaiwanLocal
      ? RECOMMENDATION_MODE_AUTO
      : this.productRecommendationMode;
    // The hand-picked add-ons. Always sent — empty on auto, when the toggle is
    // off, or on TWTW — so a previously saved list is actively cleared, not
    // left behind. An empty list makes the storefront fall back to same-series
    // suggestions.
    optionJson['product_recommendations_list'] = this.isTaiwanLocal
      ? []
      : this.buildRecommendationList();

    // Quantity Lock — the product ids whose stepper the checkout page renders
    // read-only. Not offered on TWTW, which saves an empty list there.
    optionJson['locked_quantity_products'] = this.buildLockedQuantityList();

    // PWP — per promotion product, the quantity the checkout page pre-selects
    // and whether it shows the row. Not offered on TWTW, which saves an empty
    // list there.
    optionJson['pwp_products'] = this.buildPwpProductList();

    const voucherStatus = this.generateForm.get('voucher_status')?.value ? 1 : 0;
    this.body['voucher_status'] = voucherStatus;
    optionJson['voucher_status'] = voucherStatus;

    this.body['option_json'] = optionJson;

    this.applyProductLinkToBody();

    this.buttonFreeze = true;
    this.svc
      .getCheckUrl(
        this.url,
        this.selectedCountry.toUpperCase(),
        languageCode,
        this.bearer
      )
      .subscribe(
        (res) => {
          let me = this;
          me.urlData = res['data'];
          if (me.urlData == false) {
            this.linkGenerateSuccessful();
          } else {
            this.utils.notify.showError(this.utils.translate('Page URL exist'));
            // this.getErrorMessage(null, 'Page URL exist');
            this.checkoutSuccess = false;
            this.buttonFreeze = false;
          }
        },
        (error) => {
          console.log(error);
          this.utils.notify.showError(
            error.error.messages?.error,
            this.utils.translate('Update failed!')
          );
          // this.getErrorMessage(error);
          this.checkoutSuccess = false;
          this.buttonFreeze = false;
        }
      );
  }

  updatelink() {
    if (!this.selectedProducts.length) {
      this.utils.msgBox.open({
        title: this.utils.translate('Uh oh!'),
        icon: 'warning',
        message: this.utils.translate('Please select at least 1 product.'),
      });
      return;
    }

    let languageCode = this.currentLanguage['language_code'];
    this.page_title = this.generateForm.get('page_title').value;
    this.url = this.generateForm.get('url').value;
    this.description = this.generateForm.get('description').value;
    this.body['store_id'] = this.store_id;
    this.body['checkout_link_id'] = this.checkout_link_id;
    this.body['store_id'] = this.store_id;
    this.body['page_title'] = this.page_title;
    this.body['url'] = this.url;
    this.body['description'] = this.description;
    this.body['country_code'] = this.selectedCountry.toUpperCase();
    this.body['language_code'] = languageCode;

    let optionJson = {};
    this.appearanceOptionSchema.forEach((schema) => {
      optionJson[schema.formName] =
        this.generateForm.get(schema.formName).value ?? schema.colorOptions[0];
    });
    // Forced on under the split theme: the control is disabled there, and a
    // disabled control is excluded from form.value, so read it explicitly.
    optionJson['custom_header_enabled'] = this.isSplitCheckoutTheme
      ? true
      : this.generateForm.get('custom_header_enabled').value ?? false;
    optionJson['custom_header_html'] =
      this.generateForm.get('custom_header_html').value ?? DEFAULT_CUSTOM_HEADER_HTML;
    // The storefronts render custom_footer_html whenever it's non-empty and
    // don't read the enabled flag, so a disabled footer must save as blank —
    // otherwise the sample footer would go live on the checkout page.
    optionJson['custom_footer_enabled'] =
      this.generateForm.get('custom_footer_enabled').value === true;
    optionJson['custom_footer_html'] = optionJson['custom_footer_enabled']
      ? this.generateForm.get('custom_footer_html').value ?? ''
      : '';
    // Custom Highlight — the HTML block shown above the voucher field. The flag
    // is always sent so switching the toggle back off actually takes the block
    // off the page, and the markup goes with it either way so a dealer who
    // toggles off does not lose what they wrote. Forced off for both Taiwan
    // stores (TWTW and MYTW), matching how the card is hidden in the UI.
    optionJson['custom_highlight_enabled'] = this.isTaiwan
      ? false
      : this.generateForm.get('custom_highlight_enabled').value === true;
    optionJson['custom_highlight_html'] =
      this.generateForm.get('custom_highlight_html').value ??
      DEFAULT_CUSTOM_HIGHLIGHT_HTML;
    // Custom Note — the HTML block shown under the order total. Same handling
    // as Custom Highlight above, including being forced off for Taiwan.
    optionJson['custom_note_enabled'] = this.isTaiwan
      ? false
      : this.generateForm.get('custom_note_enabled').value === true;
    optionJson['custom_note_html'] =
      this.generateForm.get('custom_note_html').value ?? DEFAULT_CUSTOM_NOTE_HTML;
    // Product Recommendations is not offered for the Taiwan (TWTW) store — force
    // it off in the payload there, matching how the card is hidden in the UI.
    optionJson['product_recommendations'] = this.isTaiwanLocal
      ? false
      : (this.generateForm.get('product_recommendations').value ?? true);
    optionJson['product_recommendations_mode'] = this.isTaiwanLocal
      ? RECOMMENDATION_MODE_AUTO
      : this.productRecommendationMode;
    // The hand-picked add-ons. Always sent — empty on auto, when the toggle is
    // off, or on TWTW — so a previously saved list is actively cleared, not
    // left behind. An empty list makes the storefront fall back to same-series
    // suggestions.
    optionJson['product_recommendations_list'] = this.isTaiwanLocal
      ? []
      : this.buildRecommendationList();

    // Quantity Lock — the product ids whose stepper the checkout page renders
    // read-only. Not offered on TWTW, which saves an empty list there.
    optionJson['locked_quantity_products'] = this.buildLockedQuantityList();

    // PWP — per promotion product, the quantity the checkout page pre-selects
    // and whether it shows the row. Not offered on TWTW, which saves an empty
    // list there.
    optionJson['pwp_products'] = this.buildPwpProductList();

    const voucherStatus = this.generateForm.get('voucher_status')?.value ? 1 : 0;
    this.body['voucher_status'] = voucherStatus;
    optionJson['voucher_status'] = voucherStatus;

    this.body['option_json'] = optionJson;

    this.applyProductLinkToBody();

    this.body['product'] = this.checkProductList(this.selectedProducts);

    this.buttonFreeze = true;
    if (this.page_title === '' || this.url === '') {
      this.buttonFreeze = false;
      this.utils.notify.showError(
        this.utils.translate('Please fill in the required field(s)'),
        this.utils.translate('Update failed!')
      );
      // this.getErrorMessage(null, 'Please fill in the required field(s)');
      // this.toastError = true;
    } else {
      this.loading = true;
      this.svc.updateCheckoutLink(this.bearer, this.body).subscribe(
        (res) => {
          this.utils.notify.showSuccess(
            this.utils.translate('Updated Successfully!')
          );
          setTimeout(() => {
            this.router.navigateByUrl('/dashboard/express-checkout');
          }, 3000);
        },
        (error) => {
          this.loading = false;
          // this.getErrorMessage(error);
          this.utils.notify.showError(error);
          // this.toastError = true;
          this.buttonFreeze = false;
        }
      );
    }
  }

  linkGenerateSuccessful() {
    console.log('linkGenerateSuccessful', this.body);

    this.svc.postGenerateLink(this.body, this.bearer).subscribe(
      (res) => {
        let me = this;
        const data = res['data'] || {};
        me.checkout_link_id = data['id'];
        this.expressUrl = data['express_url'];
        this.captureProductLinkResult(data);
        this.checkoutSuccess = true;
        this.loading = true;

        const info = JSON.parse(localStorage.getItem('5s-info') || '{}');
        this.svc.publish_event({
          data: {
            d_id: info.d_id ?? '',
            type: 'Create Express Success',
            distinct_id: info.d_id ?? '',
            dealer_username: info.dealer_name ?? '',
            store_id: this.store_id,
            checkout_id: me.checkout_link_id,
            checkout_title: this.body['page_title'],
            checkout_url: this.body['url'],
            checkout_desc: this.body['description'],
            checkout_product: this.body['product'],
            checkout_country: this.body['country_code'],
            checkout_language: this.body['language_code'],
            platform: 'dashboard',
          },
          application: 'dashboard',
          driver: 'mixpanel',
        }).subscribe();

        // Call Nexus API to get SSO link for social media sharing
        if (this.store_id) {
          const nexusBody = {
            store_id: this.store_id,
            checkout_link_id: me.checkout_link_id,
            promotional_text: this.description || ''
          };

          this.svc.getNexusInfo(nexusBody, this.bearer).subscribe(
            (nexusRes) => {
              if (nexusRes['data']?.sso_link) {
                this.nexusSsoLink = nexusRes['data'].sso_link;
              }
            },
            (nexusError) => {
              console.log('Nexus API error:', nexusError);
              // Don't show error to user, just log it
            }
          );
        }

        setTimeout(() => { }, 2000);
      },
      (error) => {
        this.loading = false;
        console.log(error);

        const info = JSON.parse(localStorage.getItem('5s-info') || '{}');
        this.svc.publish_event({
          data: {
            d_id: info.d_id ?? '',
            type: 'Create Express Fail',
            distinct_id: info.d_id ?? '',
            dealer_username: info.dealer_name ?? '',
            store_id: this.store_id,
            checkout_title: this.body['page_title'],
            checkout_url: this.body['url'],
            checkout_desc: this.body['description'],
            checkout_product: this.body['product'],
            checkout_country: this.body['country_code'],
            checkout_language: this.body['language_code'],
            platform: 'dashboard',
            error_response: error?.error?.messages?.error ?? error?.error?.message ?? '',
          },
          application: 'dashboard',
          driver: 'mixpanel',
        }).subscribe();

        this.utils.notify.showError(
          error.error.messages?.error,
          this.utils.translate('Create failed!')
        );
        // this.getErrorMessage(error, 'Create failed!');
        // this.toastError = true;
        this.buttonFreeze = false;
      }
    );
  }

  copyGenerateLink() {
    this.copyTextToClipboard(this.expressUrl);
  }

  copyProductLink() {
    this.copyTextToClipboard(this.productLinkResultUrl);
  }

  private copyTextToClipboard(text: string) {
    let selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = text || '';
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);

    this.utils.notify.showSuccess(
      this.utils.translate('Link copied to clipboard')
    );
  }

  shareToSocialMedia() {
    if (this.nexusSsoLink) {
      // Track Mixpanel event
      this.trackShareEvent('Express Checkout Share Success (After Creation)');
      window.open(this.nexusSsoLink, '_blank');
    }
  }

  getErrorMessage(error = null, message = null) {
    if (error?.error?.messages?.error) {
      this.utils.notify.showError(error?.error?.messages?.error);
    } else if (error?.error?.message) {
      let err = Object.values(error?.error?.message);
      let msg = '';
      let i = 0;
      err.forEach((value) => {
        msg += this.utils.translate(value);
        i++;
        if (i < err.length) {
          msg += '\n';
        }
      });
      this.utils.notify.showError(msg);
    } else {
      this.utils.notify.showError(
        message ?? this.utils.translate('Update failed!')
      );
    }
    this.utils.msgBox.open({
      message: message ?? this.utils.translate('Update failed!'),
      title: this.utils.translate('Error'),
      icon: 'error',
      contentClassList: 'whitespace-pre-line',
    });
  }

  filterNonUrlCharacters(event) {
    let inputElement = event.target;
    let currentValue = inputElement.value;
    let sanitizedValue = currentValue.replace(/--+/g, '-');
    if (sanitizedValue[0] === '-') sanitizedValue = sanitizedValue.substr(1);
    sanitizedValue = sanitizedValue.trim().replace(/[^a-zA-Z0-9-]/g, '');
    inputElement.value = sanitizedValue;
    this.generateForm.get('url').setValue(sanitizedValue);
  }

  private checkProductList(body) {
    return body.flatMap((item) => {
      // TWTW: keep original payload shape — no is_subscription field
      if (this.isTaiwanLocal) {
        return [{ product_id: item.product_id, quantity: item.quantity }];
      }
      const p = this.getProductData(item.product_id);
      if (p?.isSubscriptionPlan) {
        // item.product_id is the card p_id (= sub.id); each card is its own subscription.
        const rawSub = this.subscriptionApiProducts.find((s) => +s.id === +item.product_id);
        const cycleProducts = rawSub?.cycles?.[0]?.products;
        if (cycleProducts?.length) {
          return [{
            id: rawSub.id,
            quantity: item.quantity,
            is_subscription: 1,
            products: cycleProducts.map((cp) => ({
              product_id: cp.p_id,
              quantity: +cp.quantity || 1,
            })),
          }];
        }
        return [{
          id: rawSub?.id,
          quantity: item.quantity,
          is_subscription: 1,
          products: [{
            product_id: item.product_id,
            quantity: 1,
          }],
        }];
      }
      return [{
        product_id: item.product_id,
        quantity: item.quantity,
        is_subscription: 0,
      }];
    });
  }

  trackShareEvent(eventType: string) {
    const info = JSON.parse(localStorage.getItem('5s-info') || '{}');
    const dealerId = info.d_id || '';

    const dataEvent = {
      data: {
        d_id: dealerId,
        type: eventType,
        distinct_id: dealerId,
      },
      application: 'dashboard',
      driver: 'mixpanel',
    };

    this.svc.publish_event(dataEvent).subscribe(
      (res) => {
        console.log('Mixpanel event tracked:', { res });
      },
      (error) => {
        console.log('Mixpanel tracking error:', { error });
      }
    );
  }
}
